/* eslint-disable @next/next/no-img-element -- AI check galleries use proxied auction images with runtime fallback behavior. */
"use client";

import type { RepairPartsFields, RepairPartsSourcing } from "../../lib/repair-parts";
import { RepairReportExportButton } from "../../components/repair-report-export-button";
import { RepairPartSources } from "../../components/repair-part-sources";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { track } from "@carpybara/analytics/client";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { useShell } from "../../components/page-shell";
import { AiScanCapybaraScene } from "../../components/ai-scan-capybara-scene";
import { ScrollReveal } from "../../components/scroll-reveal";
import { Button, pressableClassName } from "@carpybara/ui/shadcn/button";
import { Input } from "@carpybara/ui/shadcn/input";
import { AI_SCORE_THRESHOLDS_EU } from "@carpybara/ui/ai-score";
import { InspectionBayPanel, type InspectionBayLabels, type InspectionBayMode } from "@carpybara/ui/iso";
import Link from "next/link";
import {
  AlertTriangle,
  ArrowUpRight,
  Car,
  ClipboardCheck,
  Info,
  Maximize2,
  Search,
  Sparkles,
  Tag,
  Truck,
  Users,
  Zap,
} from "lucide-react";
import { ExportPdfButton } from "../../components/export-pdf-button";
import {
  NotRepairablePriceNotice,
  PriceVerdictGauge,
  type PriceVerdict,
} from "../../components/price-verdict-gauge";
import { stripRepairPriceMentions } from "../../lib/repairability";
import {
  getIaaiImageKey,
  isPerPhotoUnanalyzed,
  normalizeIaaiImageUrl,
} from "../../lib/iaai-car";
import { normalizeIaaiInterior360 } from "../../lib/iaai-360";
import { Interior360Viewer } from "../../components/interior-360-viewer";
import { AppModal } from "../../components/app-modal";
import {
  useTranslatedAi,
  useTranslatedEuAiAnalysis,
  type TranslatableEuAiAnalysis,
} from "../../lib/use-translated-ai";
import { VinCheckLink } from "../../components/vin-check-link";
import { proxiedImageSrc } from "../../lib/image-proxy";
import { useBayPhotoUrls } from "../../lib/bay-photos";
import { bcp47, currencyDisplayFor } from "../../lib/locale";
import {
  formatNextFreeCheckDate,
  freeNoChecksBody as freeNoChecksBodyFor,
} from "../../lib/ai-check-free-quota";
import { markAiCheckUpdatedListing } from "../../lib/ai-check-navigation";
import {
  EU_CATALOG_LISTING_ID_RE,
  isUkSalvageAiInput,
  isSupportedCatalogAuctionUrl,
  isSupportedVehicleHost,
  parseCarIdentifier,
  type ParsedCarIdentifier,
} from "../../lib/car-id-input";
import { detailPathForListingId } from "../../lib/listings/detail-path";
import {
  backToListLabelKey,
  parseBackToPath,
} from "../../lib/listings/back-to-list";
import { VehicleQuickActions } from "../../components/vehicle-quick-actions";
import {
  AuctionSalesHistory,
  type AuctionSaleRecord,
} from "../../components/auction-sales-history";
import { ImageFullscreenPreview } from "../../components/image-fullscreen-preview";
import {
  PhotoRepairLineList,
  RepairItemPhotoRefs,
} from "../../components/photo-repair-lines";
import {
  photoRepairLines,
  repairItemPhotoNumbers,
  type RepairItemPhotoLinks,
} from "../../lib/damage-inventory";
import type { ArchivedFinalBidComparableSummary } from "../../lib/archived-final-bid-comparables";

type GalleryDamageByImageItem = {
  image_url: string;
  damage_description: string;
};

type RepairItem = RepairPartsFields &
  RepairItemPhotoLinks & {
    part: string;
    action: "repair" | "replace";
    estimated_cost_min_usd: number;
    estimated_cost_max_usd: number;
    notes: string;
    repair_details_url?: string | null;
  };

/** What a photo card needs from a repair item to list it under the photo. */
type PhotoRepairItem = Pick<
  RepairItem,
  "part" | "estimated_cost_min_usd" | "estimated_cost_max_usd"
> &
  RepairItemPhotoLinks;

type AiCheckResponse = {
  analysis_kind?: "salvage_us" | "eu_buy_now";
  explain: string;
  photo_damage_visible: string;
  photo_vehicle_condition: string;
  eu_analysis?: TranslatableEuAiAnalysis | null;
  ai_provider?:
    | "local"
    | "gemini"
    | "groq"
    | "openai"
    | "deepseek"
    | "moonshot";
  ai_model?: string;
  repair_recommendations?: string;
  repair_analysis_id?: string | null;
  parts_sourcing?: RepairPartsSourcing | null;
  repair_items?: RepairItem[];
  repair_total_min_usd?: number;
  repair_total_max_usd?: number;
  ai_image_url?: string | null;
  image_urls?: string[] | null;
  gallery_damage_by_image?: GalleryDamageByImageItem[] | null;
  match_by?: "vin" | "url" | "scraped";
  listing_source?: "iaai" | "copart" | "autoria" | "eu";
  listing_id?: string | null;
  listing_title: string | null;
  listing_vin?: string | null;
  listing_stock_number?: string | null;
  listing_url: string;
  repairability?: string | null;
  price_verdict?: PriceVerdict | null;
  price_verdict_source?:
    | "archived_final_bid_quantiles"
    | "exact_vin_sold_history"
    | "ai_estimate"
    | "not_repairable";
  archived_final_bid_comparables?: ArchivedFinalBidComparableSummary | null;
  archived_final_bid_lookup?: {
    status?: "available" | "empty" | "insufficient" | "unavailable" | "not_applicable";
    code?: string | null;
    retryable?: boolean;
  } | null;
  buy_now_price_usd?: number | null;
  current_bid_usd?: number | null;
  buy_now_price_coefficient?: number | null;
  is_flip?: boolean | null;
  flip_warning?: string | null;
  bidcars_sales_history?: AuctionSaleRecord[] | null;
  /** Locale the AI actually produced output in (currently "en" or "uk"). */
  generated_locale?: string;
  /** Remaining AI checks after this request. */
  remaining?: number;
  /**
   * True when the analysis was run on only a single image because the user
   * acknowledged the accuracy trade-off via the confirmation modal. Drives
   * the "limited image data" warning banner on the result page.
   */
  single_image_analysis?: boolean;
  /**
   * True when the listing had a single still photo but the backend filled in
   * the gallery with 8 sampled 360 exterior frames. Result is full-quality;
   * surfaced for debugging / future UX hints (e.g. "analyzed from 360 spin").
   */
  used_exterior_360?: boolean;
  /** True when the equirectangular interior 360 panorama was attached. */
  used_interior_360?: boolean;
  /**
   * URL of the interior 360 equirectangular panorama (when the listing has
   * one). Frontend swaps the matching `gallery_damage_by_image` slot for
   * an interactive <Interior360Viewer /> instead of a flat thumbnail.
   */
  interior_360_image_url?: string | null;
  /** Auction history data (if VIN has previous auctions) */
  vin_history?: {
    risk_level: string;
    sale_count: number;
    previous_damages: string[];
    damage_escalation: boolean;
    damage_escalation_detail?: string;
    score_penalty: number;
    price_adjustment_factor: number;
    warning: string;
  } | null;
};

type AiCheckStreamEvent =
  | { type: "status"; stage: string; message?: string }
  | { type: "partial"; stage: string; patch: Partial<AiCheckResponse> }
  | {
      type: "done";
      status: number;
      payload: AiCheckResponse & {
        error?: string;
        detail?: string;
        requires_confirmation?: boolean;
        requires_limited_images_confirmation?: boolean;
        requires_undamaged_confirmation?: boolean;
        single_image?: boolean;
      };
    }
  | {
      type: "error";
      status: number;
      payload: {
        error?: string;
        detail?: string;
        requires_confirmation?: boolean;
        requires_limited_images_confirmation?: boolean;
        requires_undamaged_confirmation?: boolean;
        single_image?: boolean;
      };
    };

/**
 * Compact plain-text summary of the current AI analysis for the Carpy chat
 * assistant. Published via a hidden data-chat element so Carpy can answer
 * "is it worth buying?" using this page's real verdict instead of asking the
 * user to re-enter the car. Kept short (~900 chars) and defensive — every
 * field is optional depending on source (US salvage vs EU buy-now).
 */
function buildAiCheckChatSummary(r: AiCheckResponse): string {
  const bits: string[] = [];
  const usd = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
  if (r.listing_source) bits.push(`source: ${r.listing_source}`);
  const asking = r.buy_now_price_usd ?? r.current_bid_usd ?? null;
  if (asking != null) {
    bits.push(
      `${r.buy_now_price_usd ? "buy-now price" : "current bid"}: ${usd(asking)}`,
    );
  }
  const pv = r.price_verdict;
  if (pv) {
    bits.push(
      `market value ~${usd(pv.market_value_clean_usd)}; great price ≤${usd(pv.great_price_usd)}; fair ${usd(pv.fair_price_min_usd)}–${usd(pv.fair_price_max_usd)}; overpay ≥${usd(pv.overpay_price_usd)}`,
    );
    if (asking != null) {
      bits.push(
        asking <= pv.great_price_usd
          ? "the asking price is a GREAT deal vs market"
          : asking <= pv.fair_price_max_usd
            ? "the asking price is FAIR vs market"
            : "the asking price looks HIGH / overpriced vs market",
      );
    }
  }
  if (r.repair_total_min_usd || r.repair_total_max_usd) {
    bits.push(
      `estimated repair ${usd(r.repair_total_min_usd || 0)}–${usd(r.repair_total_max_usd || 0)}`,
    );
  }
  if (r.is_flip) {
    bits.push(
      `FLIP RISK DETECTED: ${(r.flip_warning || "possibly bought damaged, given cosmetic-only repairs, and relisted at a markup").slice(0, 200)}`,
    );
  }
  const vh = r.vin_history;
  if (vh) {
    bits.push(
      `auction-history risk: ${vh.risk_level} (previously listed/sold ${vh.sale_count}x)`,
    );
    if (vh.previous_damages?.length) {
      const counts = new Map<string, number>();
      for (const d of vh.previous_damages)
        counts.set(d, (counts.get(d) ?? 0) + 1);
      const dmg = [...counts.entries()]
        .slice(0, 6)
        .map(([d, n]) => (n > 1 ? `${d} ×${n}` : d))
        .join(", ");
      bits.push(`previous damages: ${dmg}`);
    }
    if (vh.damage_escalation) {
      bits.push(
        `damage escalated across auctions${vh.damage_escalation_detail ? ` (${vh.damage_escalation_detail})` : ""}`,
      );
    }
  }
  if (r.photo_vehicle_condition) {
    bits.push(
      `observed condition: ${String(r.photo_vehicle_condition).replace(/\s+/g, " ").slice(0, 180)}`,
    );
  }
  return bits.join("; ").slice(0, 900);
}

const isEuBuyNowAiInput = (
  raw: string,
  parsed: ParsedCarIdentifier | null,
  listingIdHint?: string,
) => {
  const hintedId = listingIdHint?.trim() || parsed?.listingId || "";
  if (
    isUkSalvageAiInput({
      listingId: hintedId,
      source: parsed?.source,
      rawInput: raw,
    })
  ) {
    return false;
  }
  if (hintedId && EU_CATALOG_LISTING_ID_RE.test(hintedId)) return true;
  if (parsed?.kind === "auction_url" && isSupportedCatalogAuctionUrl(raw)) {
    return true;
  }
  return isSupportedCatalogAuctionUrl(raw);
};

const createPartialAiCheckResult = (
  patch: Partial<AiCheckResponse>,
): AiCheckResponse => ({
  explain: "",
  photo_damage_visible: "",
  photo_vehicle_condition: "",
  listing_title: null,
  listing_url: "#",
  ...patch,
});

const mergeAiCheckPatch = (
  current: AiCheckResponse | null,
  patch: Partial<AiCheckResponse>,
): AiCheckResponse => ({
  ...createPartialAiCheckResult(current ?? {}),
  ...patch,
});

const hasCopy = (value?: string | null) => Boolean(value?.trim());

const AiCheckStreamingProgress = ({
  result,
  stage,
  message,
  shouldReduceMotion,
}: {
  result: AiCheckResponse;
  stage: string | null;
  message: string | null;
  shouldReduceMotion: boolean | null;
}) => {
  const { t } = useShell();
  const isEuAnalysis = result.analysis_kind === "eu_buy_now";
  const imageCount =
    result.image_urls?.length ?? (result.ai_image_url?.trim() ? 1 : 0);
  const activeMessage =
    message ??
    (isEuAnalysis
      ? t("aiCheck.progress.fallbackEu")
      : t("aiCheck.progress.fallbackUs"));
  const stageTone =
    stage === "eu-analysis"
      ? t("aiCheck.progress.stageEuReport")
      : isEuAnalysis
        ? t("aiCheck.progress.stageDeal")
        : stage === "scoring" || stage === "summary" || stage === "repairs"
          ? t("aiCheck.progress.stageReport")
          : stage === "analyzing-photos"
            ? t("aiCheck.progress.stagePhotos")
            : t("aiCheck.progress.stagePreparing");

  const steps = [
    {
      label: t("aiCheck.progress.listingFound"),
      detail: result.listing_title ?? t("aiCheck.progress.vehicleReady"),
      done: Boolean(result.listing_title),
    },
    {
      label: t("aiCheck.progress.photosPrepared"),
      detail: imageCount
        ? t("aiCheck.progress.photosQueued", { count: imageCount })
        : t("aiCheck.progress.preparingPhotos"),
      done: imageCount > 0,
    },
    {
      label: stageTone,
      detail: activeMessage,
      done: false,
      active: true,
    },
  ];

  return (
    <m.div
      className="rounded-2xl border border-(--accent)/25 bg-(--accent-bg) p-4"
      initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        {result.ai_image_url ? (
          <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-(--border) bg-(--bg-card)">
            <img
              src={proxiedImageSrc(result.ai_image_url, {
                variant: "thumbnail",
              })}
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <m.span
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-(--accent)/35 bg-(--accent)/10 text-(--accent)"
              animate={
                shouldReduceMotion
                  ? undefined
                  : { rotate: [0, 10, -10, 0], scale: [1, 1.08, 1] }
              }
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Sparkles className="h-4 w-4" aria-hidden />
            </m.span>
            <p className="text-sm font-semibold text-(--text-primary)">
              {t("aiCheck.progress.analysisInProgress")}
            </p>
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-3">
            {steps.map((step, index) => (
              <div
                key={step.label}
                className="rounded-xl border border-(--border) bg-(--bg-card)/70 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      step.done
                        ? "bg-(--status-success)"
                        : step.active
                          ? "bg-(--accent)"
                          : "bg-(--text-faint)"
                    }`}
                  />
                  <span className="truncate text-xs font-bold uppercase tracking-wide text-(--text-primary)">
                    {step.label}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs leading-snug text-(--text-muted)">
                  {step.detail}
                </p>
                {step.active ? (
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-(--border)">
                    <m.div
                      className="h-full rounded-full bg-(--accent)"
                      initial={{ x: "-80%" }}
                      animate={{ x: "120%" }}
                      transition={{
                        duration: 1.25,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 0.08,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </m.div>
  );
};

/**
 * "Visible grid + comet neurons + perimeter beam" backdrop for the
 * /ai-check hero.
 *
 *   1. Amber grid (28 px cells, ~8.5 % opacity) painted via CSS —
 *      visibly draws an "AI lattice" texture without being noisy.
 *      A radial mask keeps the center fully transparent so the text
 *      stays clean; grid surfaces in the outer ~40 % of the section.
 *   2. Four "comet neurons" that streak ALONG the grid axes —
 *      horizontal at y = 7 % / 93 % and vertical at x = 4 % / 96 %.
 *      Each is a 56 px gradient line (bright head + fading tail) so
 *      you actually SEE motion, not just a tiny dot. They only ever
 *      travel in the safe edge strips outside the text area.
 *   3. The single perimeter beam (bright amber arc on a 6 s loop,
 *      conic-gradient masked to a 1 px rounded border).
 *
 * Skipped under reduced-motion.
 */
function AiCheckHeroBackdrop() {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <>
      {/* Amber grid texture — visible on the outer ~40 % of the
          section, fully transparent under the title/form area. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(245, 158, 11, 0.085) 1px, transparent 1px), linear-gradient(to bottom, rgba(245, 158, 11, 0.085) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage:
            "radial-gradient(ellipse 60% 60% at 50% 50%, transparent 5%, rgba(0,0,0,0.45) 45%, black 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 60% 60% at 50% 50%, transparent 5%, rgba(0,0,0,0.45) 45%, black 90%)",
        }}
      />
      {/* Comet neurons streaking along the grid's outer-edge lines —
          two horizontal at y = 7 % / 93 %, two vertical at x = 4 % /
          96 %. Never enter the text region. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
      >
        <CometNeuron axis="x" perpendicular="7%" duration={6} delay={0} />
        <CometNeuron
          axis="x"
          perpendicular="93%"
          duration={6.8}
          delay={2.4}
          reverse
        />
        <CometNeuron axis="y" perpendicular="4%" duration={5.4} delay={3.6} />
        <CometNeuron
          axis="y"
          perpendicular="96%"
          duration={5.8}
          delay={1.2}
          reverse
        />
      </div>
    </>
  );
}

function CometNeuron({
  axis,
  perpendicular,
  duration,
  delay,
  reverse = false,
}: {
  axis: "x" | "y";
  perpendicular: string;
  duration: number;
  delay: number;
  reverse?: boolean;
}) {
  const isX = axis === "x";
  // The bright "head" is at the END of the gradient in travel
  // direction, so the comet leads with the head and drags its tail
  // behind. Reverse flips both direction of travel and gradient.
  const gradientDir = isX
    ? reverse
      ? "to left"
      : "to right"
    : reverse
      ? "to top"
      : "to bottom";
  const trail = `linear-gradient(${gradientDir}, transparent 0%, rgba(245, 158, 11, 0.35) 55%, rgba(252, 211, 77, 1) 100%)`;
  const positionStyle = isX
    ? { top: perpendicular, width: 56, height: 2 }
    : { left: perpendicular, width: 2, height: 56 };
  const start = reverse ? "100%" : isX ? "-56px" : "-56px";
  const end = reverse ? (isX ? "-56px" : "-56px") : "100%";

  return (
    <m.span
      className="absolute rounded-full"
      style={{
        ...positionStyle,
        background: trail,
        boxShadow: "0 0 7px rgba(252, 211, 77, 0.7)",
      }}
      initial={{
        opacity: 0,
        [isX ? "left" : "top"]: start,
      }}
      animate={{
        opacity: [0, 1, 1, 0],
        [isX ? "left" : "top"]: [start, end],
      }}
      transition={{
        duration,
        repeat: Infinity,
        delay,
        ease: "linear",
        times: [0, 0.08, 0.92, 1],
      }}
    />
  );
}

const ListingPhotoGallery = ({
  image_urls,
  ai_image_url,
  listingTitle,
}: {
  image_urls?: string[] | null;
  ai_image_url?: string | null;
  listingTitle: string | null;
}) => {
  const { t } = useShell();
  const urls = useMemo(() => {
    if (image_urls?.length) return image_urls;
    if (ai_image_url?.trim()) return [ai_image_url.trim()];
    return [];
  }, [image_urls, ai_image_url]);

  const [activeIndex, setActiveIndex] = useState(0);

  const urlsKey = urls.join("|");
  useEffect(() => {
    setActiveIndex(0);
  }, [urlsKey]);

  if (urls.length === 0) {
    return null;
  }

  const safeActive = Math.min(activeIndex, urls.length - 1);
  const mainSrc =
    normalizeIaaiImageUrl(urls[safeActive], 1200, 900) ?? urls[safeActive];
  const altBase = listingTitle ?? "Vehicle";

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-2xl border border-(--border) bg-(--bg-card)">
        <AnimatePresence mode="popLayout" initial={false}>
          <m.img
            key={mainSrc}
            src={proxiedImageSrc(mainSrc, { variant: "gallery" })}
            alt={`${altBase} — photo ${safeActive + 1} of ${urls.length}`}
            className="h-[min(420px,55vh)] w-full object-cover object-center sm:h-[420px]"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          />
        </AnimatePresence>
      </div>
      {urls.length > 1 ? (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
          {urls.map((url, i) => {
            const thumbSrc = normalizeIaaiImageUrl(url, 220, 160) ?? url;
            const isActive = i === safeActive;
            const key = getIaaiImageKey(url) ?? `${i}-${url.slice(0, 48)}`;

            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveIndex(i)}
                aria-label={`${altBase} — thumbnail ${i + 1} of ${urls.length}`}
                aria-pressed={isActive}
                className={`${pressableClassName} overflow-hidden rounded-2xl border transition hover:scale-[1.04] ${
                  isActive
                    ? "border-(--accent) ring-2 ring-(--accent)/30"
                    : "border-(--border) hover:border-(--text-muted)"
                }`}
              >
                <img
                  src={thumbSrc}
                  alt=""
                  className="h-24 w-full object-cover"
                />
              </button>
            );
          })}
        </div>
      ) : null}
      <p className="text-[11px] leading-snug text-(--text-faint)">
        {t("aiCheck.imageEditDisclaimer")}
      </p>
    </div>
  );
};

const PerPhotoDamageSection = ({
  items,
  repairItems = [],
  listingTitle,
  interior360Url,
}: {
  items: GalleryDamageByImageItem[];
  /** Inventory repair items; each is priced under its best photo only. */
  repairItems?: PhotoRepairItem[];
  listingTitle: string | null;
  /**
   * When the listing has an equirectangular interior 360 panorama, the
   * matching slot in `items` (same URL string) is swapped for the
   * interactive viewer instead of a flat thumbnail. The backend already
   * normalizes the URL identically in `interior_360_image_url`.
   */
  interior360Url?: string | null;
}) => {
  const { t } = useShell();
  const altBase = listingTitle ?? "Vehicle";
  const interior360 = interior360Url
    ? normalizeIaaiInterior360({ image_url: interior360Url })
    : null;
  const previewImages = useMemo(
    () =>
      items.map((item, i) => ({
        src: item.image_url,
        thumbnailSrc:
          normalizeIaaiImageUrl(item.image_url, 220, 160) ?? item.image_url,
        alt: `${altBase} — ${t("aiCheck.damagePhoto")} ${i + 1}`,
        description: isPerPhotoUnanalyzed(item.damage_description)
          ? null
          : item.damage_description,
      })),
    [altBase, items, t],
  );

  return (
    <div className="space-y-4">
      <ScrollReveal>
        <div>
          <h2 className="text-lg font-bold text-(--text-primary)">
            {t("aiCheck.perPhotoDamage")}
          </h2>
          <p className="mt-1 text-xs text-(--text-muted)">
            {t("aiCheck.perPhotoDamageHint")}
          </p>
          <p className="mt-2 text-[11px] leading-snug text-(--text-faint)">
            {t("aiCheck.imageEditDisclaimer")}
          </p>
        </div>
      </ScrollReveal>
      <ImageFullscreenPreview
        images={previewImages}
        title={altBase}
        trigger={({ openAt }) => (
          <div className="space-y-6">
            {items.map((item, i) => {
              const src =
                normalizeIaaiImageUrl(item.image_url, 1200, 900) ??
                item.image_url;
              const key =
                getIaaiImageKey(item.image_url) ??
                `${i}-${item.image_url.slice(0, 48)}`;
              const isInterior360 =
                interior360 != null && item.image_url === interior360Url;
              const unanalyzed = isPerPhotoUnanalyzed(item.damage_description);

              return (
                <ScrollReveal key={key}>
                  <div className="grid gap-4 rounded-2xl border border-(--border) bg-(--bg-card) p-4 md:grid-cols-2 md:items-start md:gap-6">
                    {isInterior360 && interior360 ? (
                      // Interactive equirectangular viewer — same component the
                      // /vehicles detail page uses. Sized to roughly match the
                      // 4:3 thumbnail slots so the surrounding grid stays tidy.
                      <div className="group relative">
                        <Interior360Viewer
                          interior360={interior360}
                          title={altBase}
                          t={t}
                          showHeader={false}
                          containerClassName="relative h-[320px] sm:h-[360px] md:h-[420px] rounded-xl overflow-hidden"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => openAt(i)}
                          aria-label="Open fullscreen"
                          className="absolute right-3 top-3 rounded-full bg-black/60 text-white opacity-0 shadow-lg backdrop-blur-sm hover:bg-black/80 hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
                        >
                          <Maximize2
                            className="h-4 w-4 text-white"
                            aria-hidden
                          />
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openAt(i)}
                        aria-label="Open fullscreen"
                        className={`${pressableClassName} group relative overflow-hidden rounded-2xl border border-(--border) bg-(--bg-card) text-left`}
                      >
                        <img
                          src={proxiedImageSrc(src, { variant: "card" })}
                          alt={`${altBase} — ${t("aiCheck.perPhotoDamage")} ${i + 1}`}
                          className="aspect-4/3 w-full object-cover object-center transition-transform duration-200 group-hover:scale-[1.015]"
                        />
                        <span className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white opacity-0 shadow-lg backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                          <Maximize2
                            className="h-4 w-4 text-white"
                            aria-hidden
                          />
                        </span>
                      </button>
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-(--text-muted)">
                        {isInterior360
                          ? t("vehicles.detail.interior360")
                          : t("aiCheck.damagePhoto")}{" "}
                        · {i + 1}/{items.length}
                      </p>
                      <p
                        className={`mt-2 text-sm leading-relaxed ${
                          unanalyzed
                            ? "text-(--text-muted)"
                            : "text-(--text-secondary)"
                        }`}
                      >
                        {unanalyzed
                          ? t("aiCheck.noPerPhotoCaption")
                          : item.damage_description}
                      </p>
                      <PhotoRepairLineList
                        lines={photoRepairLines(
                          repairItems,
                          item.image_url,
                          items.map((photo) => photo.image_url),
                        )}
                        formatRange={(minUsd, maxUsd) => `$${minUsd}-$${maxUsd}`}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        )}
      />
    </div>
  );
};

const typingChunkSize = (text: string) => {
  if (text.length > 900) return 12;
  if (text.length > 450) return 8;
  if (text.length > 220) return 5;
  return 3;
};

function isEditableAiCheckTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (!target.isConnected) return false;
  const tagName = target.tagName.toLowerCase();
  if (target.isContentEditable) return true;
  if (tagName === "input" || tagName === "select" || tagName === "textarea") {
    return true;
  }
  return Boolean(target.closest('[contenteditable="true"], [role="textbox"]'));
}

const StreamingText = ({
  text,
  active,
  complete,
  emptyText,
  className,
  onDone,
}: {
  text: string;
  active: boolean;
  complete?: boolean;
  emptyText?: string;
  className?: string;
  onDone?: () => void;
}) => {
  const shouldReduceMotion = useReducedMotion();
  const cleanText = text.trim();
  const fallbackText = cleanText || emptyText || "—";
  const [visibleText, setVisibleText] = useState(
    complete || shouldReduceMotion ? fallbackText : "",
  );
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    if (!active) {
      setVisibleText(complete ? fallbackText : "");
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
      };
    }

    if (complete || shouldReduceMotion || !cleanText) {
      setVisibleText(fallbackText);
      timer = setTimeout(() => onDoneRef.current?.(), 80);
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
      };
    }

    let index = 0;
    const chunk = typingChunkSize(cleanText);
    setVisibleText("");

    const tick = () => {
      if (cancelled) return;
      index = Math.min(cleanText.length, index + chunk);
      setVisibleText(cleanText.slice(0, index));
      if (index >= cleanText.length) {
        timer = setTimeout(() => onDoneRef.current?.(), 180);
        return;
      }
      timer = setTimeout(tick, 14);
    };

    timer = setTimeout(tick, 120);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    active,
    cleanText,
    complete,
    emptyText,
    fallbackText,
    shouldReduceMotion,
  ]);

  return (
    <p className={className}>
      {visibleText}
      {active &&
      !complete &&
      cleanText &&
      visibleText.length < cleanText.length ? (
        <span className="ml-0.5 inline-block h-4 w-1 translate-y-0.5 animate-pulse rounded-full bg-(--accent)" />
      ) : null}
    </p>
  );
};

const ProgressivePhotoDamageSection = ({
  items,
  repairItems = [],
  listingTitle,
  interior360Url,
  activeIndex,
  completedCount,
  revealAll,
  autoScrollEnabled,
  onPhotoDone,
}: {
  items: GalleryDamageByImageItem[];
  repairItems?: PhotoRepairItem[];
  listingTitle: string | null;
  interior360Url?: string | null;
  activeIndex: number;
  completedCount: number;
  revealAll: boolean;
  autoScrollEnabled: boolean;
  onPhotoDone: (index: number) => void;
}) => {
  const { t } = useShell();
  const shouldReduceMotion = useReducedMotion();
  const refs = useRef<Array<HTMLDivElement | null>>([]);
  const altBase = listingTitle ?? "Vehicle";
  const interior360 = interior360Url
    ? normalizeIaaiInterior360({ image_url: interior360Url })
    : null;
  const previewImages = useMemo(
    () =>
      items.map((item, i) => ({
        src: item.image_url,
        thumbnailSrc:
          normalizeIaaiImageUrl(item.image_url, 220, 160) ?? item.image_url,
        alt: `${altBase} — ${t("aiCheck.damagePhoto")} ${i + 1}`,
        description: isPerPhotoUnanalyzed(item.damage_description)
          ? null
          : item.damage_description,
      })),
    [altBase, items, t],
  );

  useEffect(() => {
    if (!autoScrollEnabled || activeIndex < 0) return;
    refs.current[activeIndex]?.scrollIntoView({
      behavior: shouldReduceMotion ? "auto" : "smooth",
      block: "center",
    });
  }, [activeIndex, autoScrollEnabled, shouldReduceMotion]);

  if (items.length === 0) return null;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold text-(--text-primary)">
            {t("aiCheck.perPhotoDamage")}
          </h2>
          <span className="rounded-full border border-(--accent)/30 bg-(--accent-bg) px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-(--accent-text)">
            {Math.min(completedCount, items.length)}/{items.length}
          </span>
        </div>
        <p className="mt-1 text-xs text-(--text-muted)">
          {t("aiCheck.perPhotoDamageHint")}
        </p>
        <p className="mt-2 text-[11px] leading-snug text-(--text-faint)">
          {t("aiCheck.imageEditDisclaimer")}
        </p>
      </div>
      <ImageFullscreenPreview
        images={previewImages}
        title={altBase}
        trigger={({ openAt }) => (
          <div className="space-y-5">
            {items.map((item, i) => {
              const src =
                normalizeIaaiImageUrl(item.image_url, 1200, 900) ??
                item.image_url;
              const key =
                getIaaiImageKey(item.image_url) ??
                `${i}-${item.image_url.slice(0, 48)}`;
              const isInterior360 =
                interior360 != null && item.image_url === interior360Url;
              const isComplete = revealAll || i < completedCount;
              const isActive = !revealAll && i === activeIndex;
              const isQueued = !isComplete && !isActive;
              const unanalyzed = isPerPhotoUnanalyzed(item.damage_description);

              return (
                <m.div
                  key={key}
                  ref={(node) => {
                    refs.current[i] = node;
                  }}
                  className={`grid gap-4 rounded-2xl border bg-(--bg-card) p-4 md:grid-cols-2 md:items-start md:gap-6 ${
                    isActive
                      ? "border-(--accent)/70"
                      : isComplete
                        ? "border-(--border)"
                        : "border-(--border)/70 opacity-70"
                  }`}
                  initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: isQueued ? 0.7 : 1, y: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                >
                  {isInterior360 && interior360 ? (
                    <div className="group relative">
                      <Interior360Viewer
                        interior360={interior360}
                        title={altBase}
                        t={t}
                        showHeader={false}
                        containerClassName="relative h-[320px] sm:h-[360px] md:h-[420px] rounded-xl overflow-hidden"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => openAt(i)}
                        aria-label="Open fullscreen"
                        className="absolute right-3 top-3 rounded-full bg-black/60 text-white opacity-0 shadow-lg backdrop-blur-sm hover:bg-black/80 hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
                      >
                        <Maximize2 className="h-4 w-4 text-white" aria-hidden />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openAt(i)}
                      aria-label="Open fullscreen"
                      className={`${pressableClassName} group relative overflow-hidden rounded-2xl border border-(--border) bg-(--bg-card) text-left`}
                    >
                      <img
                        src={proxiedImageSrc(src, { variant: "card" })}
                        alt={`${altBase} — ${t("aiCheck.perPhotoDamage")} ${i + 1}`}
                        className="aspect-4/3 w-full object-cover object-center transition-transform duration-200 group-hover:scale-[1.015]"
                      />
                      <span className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white opacity-0 shadow-lg backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                        <Maximize2 className="h-4 w-4 text-white" aria-hidden />
                      </span>
                    </button>
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-(--text-muted)">
                        {isInterior360
                          ? t("vehicles.detail.interior360")
                          : t("aiCheck.damagePhoto")}{" "}
                        · {i + 1}/{items.length}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          isActive
                            ? "bg-(--accent)/15 text-(--accent)"
                            : isComplete
                              ? unanalyzed
                                ? "bg-(--bg-tertiary) text-(--text-muted)"
                                : "bg-(--status-success-bg) text-(--status-success-text)"
                              : "bg-(--bg-tertiary) text-(--text-muted)"
                        }`}
                      >
                        {isActive
                          ? "Analyzing"
                          : isComplete
                            ? unanalyzed
                              ? "No data"
                              : "Done"
                            : "Queued"}
                      </span>
                    </div>
                    {isQueued ? (
                      <div className="mt-3 space-y-2">
                        <div className="h-3 w-11/12 rounded bg-(--border)/70" />
                        <div className="h-3 w-4/5 rounded bg-(--border)/60" />
                        <div className="h-3 w-2/3 rounded bg-(--border)/50" />
                      </div>
                    ) : (
                      <StreamingText
                        text={unanalyzed ? "" : item.damage_description}
                        active={isActive}
                        complete={isComplete}
                        emptyText={t("aiCheck.noPerPhotoCaption")}
                        className={`mt-2 text-sm leading-relaxed ${
                          unanalyzed
                            ? "text-(--text-muted)"
                            : "text-(--text-secondary)"
                        }`}
                        onDone={() => onPhotoDone(i)}
                      />
                    )}
                    {isComplete ? (
                      <PhotoRepairLineList
                        lines={photoRepairLines(
                          repairItems,
                          item.image_url,
                          items.map((photo) => photo.image_url),
                        )}
                        formatRange={(minUsd, maxUsd) => `$${minUsd}-$${maxUsd}`}
                        className="mt-2"
                      />
                    ) : null}
                  </div>
                </m.div>
              );
            })}
          </div>
        )}
      />
    </div>
  );
};

const ProgressiveSummaryCard = ({
  display,
  result,
  active,
  complete,
  revealAll,
  autoScrollEnabled,
  onDone,
}: {
  display: AiCheckResponse;
  result: AiCheckResponse;
  active: boolean;
  complete: boolean;
  revealAll: boolean;
  autoScrollEnabled: boolean;
  onDone: () => void;
}) => {
  const { t } = useShell();
  const shouldReduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) {
      setStep(0);
      return;
    }
    if (autoScrollEnabled) {
      ref.current?.scrollIntoView({
        behavior: shouldReduceMotion ? "auto" : "smooth",
        block: "start",
      });
    }
  }, [active, autoScrollEnabled, shouldReduceMotion]);

  useEffect(() => {
    if (revealAll && active) onDone();
  }, [active, onDone, revealAll]);

  const revealComplete = revealAll || complete;
  const pending = !active && !revealComplete;
  const fields = [
    {
      key: "damage",
      title: t("aiCheck.damagePhoto"),
      text: display.photo_damage_visible,
      tone: "text-(--text-secondary)",
    },
    {
      key: "condition",
      title: t("aiCheck.conditionPhoto"),
      text: display.photo_vehicle_condition,
      tone: "text-(--text-secondary)",
    },
    {
      key: "summary",
      title: t("aiCheck.narrativeSummary"),
      text: display.explain || t("aiCheck.noExplanation"),
      tone: "text-(--text-muted)",
    },
  ];

  const handleStepDone = (index: number) => {
    if (index < fields.length - 1) {
      setStep((current) => Math.max(current, index + 1));
      return;
    }
    onDone();
  };

  return (
    <m.div
      ref={ref}
      className="rounded-2xl border border-(--border) bg-(--bg-secondary) px-4 py-4 text-sm"
      initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: active ? 1 : 0.65, y: 0 }}
      transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="inline-flex items-center gap-2 font-semibold text-(--text-primary)">
        <m.span
          className="inline-flex"
          animate={
            shouldReduceMotion
              ? undefined
              : { rotate: [0, 12, -12, 0], scale: [1, 1.15, 1] }
          }
          transition={{
            duration: 2.6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Sparkles className="h-4 w-4 shrink-0 text-(--accent)" aria-hidden />
        </m.span>
        {t("aiCheck.aiExplanation")}
      </div>
      <div className="mt-4 space-y-4">
        {pending ? (
          <m.div
            className="rounded-xl border border-dashed border-(--accent)/30 bg-(--accent-bg) px-3 py-3"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, ease: "easeOut" }}
          >
            <p className="text-xs font-bold uppercase tracking-wide text-(--accent)">
              {t("aiCheck.progress.summaryPendingTitle")}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-(--text-muted)">
              {t("aiCheck.progress.summaryPendingBody")}
            </p>
          </m.div>
        ) : null}

        {fields.map((field, index) => {
          const fieldActive = active && !revealComplete && step === index;
          const fieldComplete = revealComplete || step > index;
          if (!fieldActive && !fieldComplete) return null;

          return (
            <m.div
              key={field.key}
              className={
                index > 0 ? "border-t border-(--border) pt-4" : undefined
              }
              initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.26, ease: "easeOut" }}
            >
              <h3 className="text-xs font-bold uppercase tracking-wide text-(--text-muted)">
                {field.title}
              </h3>
              <StreamingText
                text={field.text}
                active={fieldActive}
                complete={fieldComplete}
                emptyText={index === 2 ? t("aiCheck.noExplanation") : "—"}
                className={`mt-1.5 leading-relaxed ${field.tone}`}
                onDone={() => handleStepDone(index)}
              />
            </m.div>
          );
        })}

        {revealComplete || complete ? (
          <m.div
            className="border-t border-(--border) pt-4"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
          >
            <h3 className="text-xs font-bold uppercase tracking-wide text-(--text-muted)">
              {t("aiCheck.repairRecommendations")}
            </h3>
            {display.repair_items && display.repair_items.length > 0 ? (
              <div className="mt-2 grid gap-2 sm:grid-cols-1 lg:grid-cols-2">
                {display.repair_items.map((item, index) => (
                  <div
                    key={`${item.part}-${index}`}
                    className="rounded-lg border border-(--border) bg-(--bg-card) px-3 py-2 transition-colors hover:border-(--accent)/35"
                  >
                    <p className="text-sm font-semibold text-(--text-primary)">
                      {item.part} -{" "}
                      {item.action === "replace"
                        ? t("aiCheck.actionReplace")
                        : t("aiCheck.actionRepair")}
                    </p>
                    <p className="text-sm text-(--text-secondary)">
                      ${item.estimated_cost_min_usd} - $
                      {item.estimated_cost_max_usd}
                    </p>
                    {item.notes ? (
                      <p className="mt-1 text-xs text-(--text-muted)">
                        {item.notes}
                      </p>
                    ) : null}
                    <RepairItemPhotoRefs
                      photos={repairItemPhotoNumbers(
                        item,
                        (display.gallery_damage_by_image ?? []).map(
                          (photo) => photo.image_url,
                        ),
                      )}
                      t={t}
                      className="mt-1"
                    />
                    {item.repair_details_url ? (
                      <a
                        href={item.repair_details_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-(--accent) hover:underline"
                      >
                        {t("aiCheck.openRepairDetails")}
                        <span className="button-icon-motion-external inline-flex shrink-0">
                          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                        </span>
                      </a>
                    ) : null}
                    <RepairPartSources item={item} analysisId={result.repair_analysis_id} index={index} vehicleTitle={result.listing_title} compact />
                  </div>
                ))}
                <p className="lg:col-span-2 text-sm font-semibold text-(--text-primary)">
                  {t("aiCheck.totalEstimatedRepairCost")}: $
                  {result.repair_total_min_usd ?? 0} - $
                  {result.repair_total_max_usd ?? 0}
                </p>
                {result.price_verdict ? (
                  <div className="lg:col-span-2">
                    <PriceVerdictGauge
                      verdict={result.price_verdict}
                      source={result.price_verdict_source}
                      archivedFinalBidComparables={
                        result.archived_final_bid_lookup?.status === "available" ||
                        result.price_verdict_source === "archived_final_bid_quantiles"
                          ? result.archived_final_bid_comparables
                          : null
                      }
                      buyNowPrice={result.buy_now_price_usd}
                      buyNowPriceCoefficient={result.buy_now_price_coefficient}
                      currentBidPrice={result.current_bid_usd}
                      className="mt-3"
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <p className="mt-1.5 leading-relaxed text-(--text-muted)">
                  {display.repair_recommendations?.trim() || "—"}
                </p>
                {result.repairability === "parts_only" ? (
                  <NotRepairablePriceNotice className="mt-3" />
                ) : null}
              </>
            )}
          </m.div>
        ) : null}
        <p className="text-xs text-(--text-faint)">{t("aiCheck.disclaimer")}</p>
      </div>
    </m.div>
  );
};

const ProgressiveAiReport = ({
  display,
  result,
}: {
  display: AiCheckResponse;
  result: AiCheckResponse;
}) => {
  const { t } = useShell();
  const items = display.gallery_damage_by_image ?? [];
  const [revealAll, setRevealAll] = useState(false);
  const [completedPhotoCount, setCompletedPhotoCount] = useState(0);
  const [activePhotoIndex, setActivePhotoIndex] = useState(-1);
  const [summaryActive, setSummaryActive] = useState(false);
  const [summaryComplete, setSummaryComplete] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const reportKey = `${result.listing_id ?? result.listing_url}`;
  const hasSummaryContent =
    hasCopy(display.photo_damage_visible) ||
    hasCopy(display.photo_vehicle_condition) ||
    hasCopy(display.explain) ||
    hasCopy(display.repair_recommendations) ||
    (display.repair_items?.length ?? 0) > 0 ||
    Boolean(result.price_verdict);
  const revealInProgress =
    !revealAll &&
    (completedPhotoCount < items.length ||
      (hasSummaryContent && !summaryComplete));

  useEffect(() => {
    setRevealAll(false);
    setCompletedPhotoCount(0);
    setActivePhotoIndex(-1);
    setSummaryActive(false);
    setSummaryComplete(false);
    setAutoScrollEnabled(true);
  }, [reportKey]);

  useEffect(() => {
    if (
      revealAll ||
      activePhotoIndex >= 0 ||
      completedPhotoCount >= items.length ||
      items.length === 0
    ) {
      return;
    }
    const timer = setTimeout(() => {
      setActivePhotoIndex(completedPhotoCount);
    }, 220);
    return () => clearTimeout(timer);
  }, [activePhotoIndex, completedPhotoCount, items.length, revealAll]);

  useEffect(() => {
    if (
      revealAll ||
      items.length > 0 ||
      summaryActive ||
      summaryComplete ||
      !hasSummaryContent
    ) {
      return;
    }
    const timer = setTimeout(() => setSummaryActive(true), 160);
    return () => clearTimeout(timer);
  }, [
    hasSummaryContent,
    items.length,
    revealAll,
    summaryActive,
    summaryComplete,
  ]);

  useEffect(() => {
    if (
      revealAll ||
      items.length === 0 ||
      activePhotoIndex >= 0 ||
      completedPhotoCount < items.length ||
      summaryActive ||
      summaryComplete ||
      !hasSummaryContent
    ) {
      return;
    }
    const timer = setTimeout(() => setSummaryActive(true), 220);
    return () => clearTimeout(timer);
  }, [
    activePhotoIndex,
    completedPhotoCount,
    hasSummaryContent,
    items.length,
    revealAll,
    summaryActive,
    summaryComplete,
  ]);

  const handlePhotoDone = useCallback(
    (index: number) => {
      if (revealAll || index !== activePhotoIndex) return;
      const nextCompleted = Math.max(completedPhotoCount, index + 1);
      setCompletedPhotoCount(nextCompleted);
      if (index < items.length - 1) {
        setTimeout(() => setActivePhotoIndex(index + 1), 180);
        return;
      }
      setActivePhotoIndex(-1);
    },
    [activePhotoIndex, completedPhotoCount, items.length, revealAll],
  );

  const handleShowFullReport = useCallback(() => {
    setRevealAll(true);
    setCompletedPhotoCount(items.length);
    setActivePhotoIndex(-1);
    setSummaryActive(true);
    setSummaryComplete(true);
    setAutoScrollEnabled(false);
  }, [items.length]);

  useEffect(() => {
    if (!autoScrollEnabled || !revealInProgress) return;

    // Listen for direct scroll intent, not the scroll event itself: our own
    // smooth scrollIntoView also emits scroll events while guiding the reveal.
    const handleScrollIntent = () => handleShowFullReport();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isEditableAiCheckTarget(event.target)
      ) {
        return;
      }
      if (
        [
          "ArrowDown",
          "ArrowUp",
          "PageDown",
          "PageUp",
          "Home",
          "End",
          " ",
          "Spacebar",
        ].includes(event.key)
      ) {
        handleShowFullReport();
      }
    };

    window.addEventListener("wheel", handleScrollIntent, { passive: true });
    window.addEventListener("touchmove", handleScrollIntent, {
      passive: true,
    });
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("wheel", handleScrollIntent);
      window.removeEventListener("touchmove", handleScrollIntent);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [autoScrollEnabled, handleShowFullReport, revealInProgress]);

  // The reveal explains itself: the page auto-scrolls to each section as it
  // lands, so a standing "the report opens step by step" note described what
  // the reader was already watching — and stayed on screen after the reveal
  // had finished. Only the skip control is left, and only while there is
  // something left to skip.
  const canSkipReveal =
    !revealAll && (!summaryComplete || completedPhotoCount < items.length);

  return (
    <div className="space-y-5">
      {canSkipReveal ? (
        <div className="flex justify-end rounded-2xl border border-(--border) bg-(--bg-card) px-4 py-3">
          <button
            type="button"
            onClick={handleShowFullReport}
            className={`${pressableClassName} inline-flex min-h-10 items-center justify-center rounded-lg border border-(--border) bg-(--bg-secondary) px-3 text-xs font-semibold text-(--text-primary) hover:border-(--accent)/45 hover:text-(--accent)`}
          >
            {t("aiCheck.progress.showFullReport")}
          </button>
        </div>
      ) : null}

      <ProgressiveSummaryCard
        display={display}
        result={result}
        active={summaryActive}
        complete={summaryComplete}
        revealAll={revealAll}
        autoScrollEnabled={autoScrollEnabled}
        onDone={() => setSummaryComplete(true)}
      />

      {items.length ? (
        <ProgressivePhotoDamageSection
          items={items}
          repairItems={
            result.repairability === "parts_only"
              ? []
              : (display.repair_items ?? [])
          }
          listingTitle={result.listing_title}
          interior360Url={result.interior_360_image_url}
          activeIndex={activePhotoIndex}
          completedCount={completedPhotoCount}
          revealAll={revealAll}
          autoScrollEnabled={autoScrollEnabled}
          onPhotoDone={handlePhotoDone}
        />
      ) : null}

      {summaryComplete && items.length === 0 ? (
        <ScrollReveal>
          <ListingPhotoGallery
            image_urls={result.image_urls}
            ai_image_url={result.ai_image_url}
            listingTitle={result.listing_title}
          />
        </ScrollReveal>
      ) : null}
    </div>
  );
};

const formatEuAiCheckMoney = (
  value: number | null | undefined,
  locale: string,
) =>
  typeof value === "number" && Number.isFinite(value)
    ? new Intl.NumberFormat(bcp47(locale), {
        style: "currency",
        currency: "EUR",
        currencyDisplay: currencyDisplayFor("EUR"),
        maximumFractionDigits: 0,
      }).format(value)
    : "—";

const euAiScoreTone = (score: number): "good" | "default" | "warn" =>
  score >= AI_SCORE_THRESHOLDS_EU.good
    ? "good"
    : score >= AI_SCORE_THRESHOLDS_EU.mid
      ? "default"
      : "warn";

const euAiToneClasses = (tone: "good" | "default" | "warn") =>
  tone === "good"
    ? {
        border: "border-(--status-success-border)",
        bg: "bg-(--status-success-bg)",
        text: "text-(--status-success-text)",
        bar: "bg-(--status-success)",
        ring: "shadow-[0_0_32px_rgba(16,185,129,0.16)]",
      }
    : tone === "warn"
      ? {
          border: "border-(--status-danger-border)",
          bg: "bg-(--status-danger-bg)",
          text: "text-(--status-danger-text)",
          bar: "bg-(--status-danger)",
          ring: "shadow-[0_0_32px_rgba(239,68,68,0.16)]",
        }
      : {
          border: "border-(--status-warning-border)",
          bg: "bg-(--status-warning-bg)",
          text: "text-(--status-warning-text)",
          bar: "bg-(--status-warning)",
          ring: "shadow-[0_0_32px_rgba(245,158,11,0.16)]",
        };

const EuProgressiveScoreBadge = ({
  score,
  classes,
}: {
  score: number;
  classes: ReturnType<typeof euAiToneClasses>;
}) => {
  const progress = Math.max(0, Math.min(100, score));

  return (
    <div
      className={`min-w-[104px] rounded-2xl border px-3 py-3 text-center shadow-xl ${classes.border} ${classes.bg} ${classes.ring}`}
      aria-label={`AI score ${score} out of 100`}
    >
      <div className="flex items-end justify-center gap-1">
        <span
          className={`text-3xl font-bold leading-none sm:text-4xl ${classes.text}`}
        >
          {score}
        </span>
        <span className="pb-0.5 text-sm font-bold leading-none text-(--text-muted)">
          /100
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${classes.bar}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const EuProgressiveChip = ({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "good" | "warn";
}) => {
  const toneClass =
    tone === "good"
      ? "border-(--status-success-border) bg-(--status-success-bg) text-(--status-success-text)"
      : tone === "warn"
        ? "border-(--status-danger-border) bg-(--status-danger-bg) text-(--status-danger-text)"
        : "border-(--border) bg-(--bg-tertiary) text-(--text-secondary)";

  return (
    <span
      className={`inline-flex min-h-7 items-center rounded-full border px-2.5 py-1 text-xs font-bold ${toneClass}`}
    >
      {children}
    </span>
  );
};

const EuProgressiveBulletList = ({ items }: { items: string[] }) => {
  const shouldReduceMotion = useReducedMotion();
  const filtered = items.filter((item) => item.trim());
  if (!filtered.length) return null;
  return (
    <m.ul
      className="mt-3 space-y-1.5 text-sm leading-relaxed text-(--text-muted)"
      initial={shouldReduceMotion ? false : "hidden"}
      animate="show"
      variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.045,
            delayChildren: 0.04,
          },
        },
      }}
    >
      {filtered.map((item, index) => (
        <m.li
          key={`${item}-${index}`}
          className="flex gap-2"
          variants={{
            hidden: { opacity: 0, y: 8 },
            show: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-(--accent)" />
          <span>{item}</span>
        </m.li>
      ))}
    </m.ul>
  );
};

const EuProgressiveDetails = ({
  children,
  visible,
}: {
  children?: ReactNode;
  visible: boolean;
}) => {
  const shouldReduceMotion = useReducedMotion();
  if (!children) return null;

  return (
    <AnimatePresence initial={false}>
      {visible ? (
        <m.div
          key="details"
          className="overflow-hidden pl-0 sm:pl-12"
          initial={shouldReduceMotion ? false : { height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={shouldReduceMotion ? undefined : { height: 0, opacity: 0 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          <m.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: "easeOut", delay: 0.03 }}
          >
            {children}
          </m.div>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
};

const euOptionalSectionHasContent = (
  section: TranslatableEuAiAnalysis["logistics_assessment"],
) =>
  Boolean(
    section &&
    (section.summary?.trim() ||
      section.positives.length > 0 ||
      section.cautions.length > 0),
  );

const ProgressiveEuSectionCard = ({
  active,
  className,
  complete,
  icon,
  label,
  title,
  summary,
  children,
  onDone,
}: {
  active: boolean;
  className?: string;
  complete: boolean;
  icon: ReactNode;
  label: string;
  title: string;
  summary: string;
  children?: ReactNode;
  onDone: () => void;
}) => {
  const { t } = useShell();
  const shouldReduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const queued = !active && !complete;

  useEffect(() => {
    if (!active) return;
    ref.current?.scrollIntoView({
      behavior: shouldReduceMotion ? "auto" : "smooth",
      block: "center",
    });
  }, [active, shouldReduceMotion]);

  return (
    <m.div
      ref={ref}
      layout
      className={`h-full rounded-2xl border bg-(--bg-card)/90 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ${className ?? ""} ${
        active
          ? "border-(--status-special-border)"
          : complete
            ? "border-(--border)"
            : "border-(--border)/70 opacity-70"
      }`}
      initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: queued ? 0.72 : 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex size-9 shrink-0 items-center justify-center rounded-xl border ${
            active
              ? "border-(--status-special-border) bg-(--status-special-bg) text-(--status-special-text)"
              : complete
                ? "border-(--status-special-border)/60 bg-(--status-special-bg) text-(--status-special)"
                : "border-(--border) bg-(--bg-tertiary) text-(--text-muted)"
          }`}
          aria-hidden
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-(--text-muted)">
              {label}
            </p>
            {active ? (
              <span className="rounded-full bg-(--status-special-bg) px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-(--status-special-text)">
                {t("aiCheck.eu.writing")}
              </span>
            ) : null}
          </div>
          <h3 className="mt-1.5 text-base font-bold leading-snug text-(--text-primary)">
            {title}
          </h3>
          {queued ? (
            <div className="mt-3 space-y-2">
              <div className="h-3 w-11/12 rounded-full bg-(--border)/70" />
              <div className="h-3 w-4/5 rounded-full bg-(--border)/55" />
              <div className="h-3 w-2/3 rounded-full bg-(--border)/45" />
            </div>
          ) : (
            <StreamingText
              text={summary}
              active={active}
              complete={complete}
              emptyText="—"
              className="mt-2 text-sm leading-relaxed text-(--text-muted)"
              onDone={onDone}
            />
          )}
        </div>
      </div>
      <EuProgressiveDetails visible={complete}>{children}</EuProgressiveDetails>
    </m.div>
  );
};

const ProgressiveEuAiReport = ({
  analysis,
  generatedLocale,
  reportKey,
}: {
  analysis: TranslatableEuAiAnalysis;
  generatedLocale?: string | null;
  imageUrls?: string[] | null;
  listingTitle?: string | null;
  reportKey: string;
}) => {
  const { t, locale } = useShell();
  const shouldReduceMotion = useReducedMotion();
  const { translated, isTranslating } = useTranslatedEuAiAnalysis(
    analysis,
    generatedLocale ?? "en",
    locale,
  );
  const display = translated ?? analysis;
  const waitForTranslation =
    isTranslating && (generatedLocale ?? "en") !== locale;
  const [revealAll, setRevealAll] = useState(false);
  const [summaryComplete, setSummaryComplete] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [completedCount, setCompletedCount] = useState(0);
  const scoreTone = euAiScoreTone(display.score);
  const scoreClasses = euAiToneClasses(scoreTone);
  const priceTone =
    display.price_assessment.verdict === "undervalued"
      ? "good"
      : display.price_assessment.verdict === "expensive"
        ? "warn"
        : "default";
  const sections: Array<{
    key: string;
    label: string;
    title: string;
    summary: string;
    icon: ReactNode;
    children?: ReactNode;
  }> = [
    {
      key: "seller",
      label: t("aiCheck.eu.sellerSection"),
      title: display.seller_assessment.title,
      summary: display.seller_assessment.summary,
      icon: <ClipboardCheck className="size-4" />,
      children: (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {display.seller_assessment.trust_score != null ? (
              <EuProgressiveChip tone="good">
                {t("aiCheck.eu.trustScore", {
                  score: display.seller_assessment.trust_score,
                })}
              </EuProgressiveChip>
            ) : null}
          </div>
          <EuProgressiveBulletList
            items={[
              ...display.seller_assessment.positives,
              ...display.seller_assessment.cautions,
            ]}
          />
        </>
      ),
    },
    {
      key: "price",
      label: t("aiCheck.eu.priceSection"),
      title: display.price_assessment.title,
      summary: display.price_assessment.summary,
      icon: <Tag className="size-4" />,
      children: (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            <EuProgressiveChip tone={priceTone}>
              {t(`aiCheck.eu.priceVerdict.${display.price_assessment.verdict}`)}
            </EuProgressiveChip>
            <EuProgressiveChip>
              {t("aiCheck.eu.market")}:{" "}
              {formatEuAiCheckMoney(
                display.price_assessment.market_avg_eur,
                locale,
              )}
            </EuProgressiveChip>
            <EuProgressiveChip>
              {t("aiCheck.eu.landed")}:{" "}
              {formatEuAiCheckMoney(
                display.price_assessment.landed_eur,
                locale,
              )}
            </EuProgressiveChip>
            <EuProgressiveChip>
              {t("aiCheck.eu.profit")}:{" "}
              {formatEuAiCheckMoney(
                display.price_assessment.estimated_profit_eur,
                locale,
              )}
            </EuProgressiveChip>
          </div>
          <EuProgressiveBulletList items={display.price_assessment.notes} />
        </>
      ),
    },
    {
      key: "vehicle",
      label: t("aiCheck.eu.vehicleSection"),
      title: display.vehicle_assessment.title,
      summary: display.vehicle_assessment.summary,
      icon: <Car className="size-4" />,
      children: (
        <EuProgressiveBulletList
          items={[
            ...display.vehicle_assessment.positives,
            ...display.vehicle_assessment.cautions,
          ]}
        />
      ),
    },
  ];

  if (
    display.logistics_assessment &&
    euOptionalSectionHasContent(display.logistics_assessment)
  ) {
    sections.push({
      key: "logistics",
      label: t("aiCheck.eu.logisticsSection"),
      title: display.logistics_assessment.title,
      summary: display.logistics_assessment.summary,
      icon: <Truck className="size-4" />,
      children: (
        <EuProgressiveBulletList
          items={[
            ...display.logistics_assessment.positives,
            ...display.logistics_assessment.cautions,
          ]}
        />
      ),
    });
  }

  if (
    display.market_fit_assessment &&
    euOptionalSectionHasContent(display.market_fit_assessment)
  ) {
    sections.push({
      key: "market-fit",
      label: t("aiCheck.eu.marketFitSection"),
      title: display.market_fit_assessment.title,
      summary: display.market_fit_assessment.summary,
      icon: <Users className="size-4" />,
      children: (
        <EuProgressiveBulletList
          items={[
            ...display.market_fit_assessment.positives,
            ...display.market_fit_assessment.cautions,
          ]}
        />
      ),
    });
  }

  sections.push({
    key: "recommendation",
    label: t("aiCheck.eu.recommendationSection"),
    title: t("aiCheck.eu.recommendation"),
    summary: display.recommendation.summary,
    icon: <Sparkles className="size-4" />,
    children: (
      <>
        <div className="mt-3">
          <EuProgressiveChip
            tone={display.recommendation.action === "buy" ? "good" : "default"}
          >
            {t(`aiCheck.eu.action.${display.recommendation.action}`)}
          </EuProgressiveChip>
        </div>
        <EuProgressiveBulletList items={display.recommendation.next_steps} />
      </>
    ),
  });

  useEffect(() => {
    setRevealAll(false);
    setSummaryComplete(false);
    setActiveIndex(-1);
    setCompletedCount(0);
  }, [locale, reportKey]);

  useEffect(() => {
    if (
      waitForTranslation ||
      revealAll ||
      !summaryComplete ||
      activeIndex >= 0 ||
      completedCount >= sections.length
    ) {
      return;
    }
    const timer = setTimeout(() => setActiveIndex(completedCount), 180);
    return () => clearTimeout(timer);
  }, [
    activeIndex,
    completedCount,
    revealAll,
    sections.length,
    summaryComplete,
    waitForTranslation,
  ]);

  const handleSectionDone = useCallback(
    (index: number) => {
      if (revealAll || index !== activeIndex) return;
      setCompletedCount((current) => Math.max(current, index + 1));
      setActiveIndex(-1);
    },
    [activeIndex, revealAll],
  );

  const handleShowFullReport = () => {
    setRevealAll(true);
    setSummaryComplete(true);
    setCompletedCount(sections.length);
    setActiveIndex(-1);
  };

  const visibleSectionCount =
    (revealAll || summaryComplete ? 1 : 0) + completedCount;
  const totalSectionCount = sections.length + 1;

  return (
    <section className="space-y-4">
      <m.div
        className="overflow-hidden rounded-2xl border border-(--status-special-border) bg-[linear-gradient(135deg,rgba(147,51,234,0.14),rgba(250,159,10,0.08),var(--bg-secondary))] shadow-[0_20px_80px_rgba(0,0,0,0.16)]"
        initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="grid gap-4 border-b border-(--status-special-border)/50 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-(--status-special-border) bg-(--status-special-bg) px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-(--status-special-text)">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              {t("aiCheck.eu.title")}
            </div>
            <h2 className="mt-3 text-lg font-bold leading-tight text-(--text-primary) sm:text-2xl">
              {t("aiCheck.eu.summarySection")}
            </h2>
            {waitForTranslation ? (
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-(--text-muted)">
                {t("aiCheck.eu.preparingReport")}
              </p>
            ) : (
              <StreamingText
                text={display.summary}
                active={!revealAll && !summaryComplete}
                complete={revealAll || summaryComplete}
                emptyText="—"
                className="mt-2 max-w-3xl text-sm leading-relaxed text-(--text-muted)"
                onDone={() => setSummaryComplete(true)}
              />
            )}
            <div className="mt-3 inline-flex max-w-3xl items-start gap-2 rounded-xl border border-(--status-info-border) bg-(--status-info-bg) px-3 py-2 text-xs font-semibold leading-relaxed text-(--status-info-text)">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{t("aiCheck.eu.dataOnlyNote")}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <EuProgressiveScoreBadge
              score={display.score}
              classes={scoreClasses}
            />
            <EuProgressiveChip tone={scoreTone}>
              {t(`aiCheck.eu.verdict.${display.verdict}`)}
            </EuProgressiveChip>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-(--text-muted)">
            <span className="text-(--accent)">
              {Math.min(visibleSectionCount, totalSectionCount)}/
              {totalSectionCount}
            </span>
            <span>{t("aiCheck.eu.sectionsReady")}</span>
          </div>
          {!revealAll && visibleSectionCount < totalSectionCount ? (
            <button
              type="button"
              onClick={handleShowFullReport}
              className={`${pressableClassName} inline-flex min-h-10 items-center justify-center rounded-lg border border-(--status-special-border) bg-(--status-special-bg) px-3 text-xs font-bold text-(--status-special-text) transition-colors hover:border-(--status-special) hover:bg-(--status-special)/15`}
            >
              {t("aiCheck.eu.showFullReport")}
            </button>
          ) : null}
        </div>
      </m.div>

      <m.div layout className="grid items-start gap-3 lg:grid-cols-2">
        {sections.map((section, index) => (
          <ProgressiveEuSectionCard
            key={section.key}
            active={!revealAll && activeIndex === index}
            className={
              sections.length % 2 === 1 && index === sections.length - 1
                ? "lg:col-span-2"
                : undefined
            }
            complete={revealAll || index < completedCount}
            icon={section.icon}
            label={section.label}
            title={section.title}
            summary={section.summary}
            onDone={() => handleSectionDone(index)}
          >
            {section.children}
          </ProgressiveEuSectionCard>
        ))}
      </m.div>
    </section>
  );
};

/**
 * In-memory cache of completed AI-check results, keyed by the (normalized)
 * query the URL carries as `?q=`. Lives at module scope so it survives the page
 * component unmounting on client-side navigation: leave /ai-check for another
 * page and come back and the finished report paints instantly instead of
 * re-running the analysis. Query-only key (no analysis_kind) because autorun
 * strips that param from the URL, and the kind is derivable from the query
 * anyway. Not persisted across a full reload — the server's `ai_check_cache`
 * still serves those fast (no credits) via a normal re-run.
 */
const AI_CHECK_RESULT_CACHE = new Map<
  string,
  { result: AiCheckResponse; locale: string }
>();
const aiCheckCacheKey = (q: string): string => q.trim().toLowerCase();

type UserChecksBalance = {
  subscription?: number;
  purchased?: number;
  free?: number;
  remaining?: number;
  tier?: "free" | "pro" | "ultra";
  resetAt?: string | null;
};

function AiCheckContent() {
  const { data: session } = useSession();
  const { t, locale, country, toast } = useShell();
  const router = useRouter();
  const searchParams = useSearchParams();
  const shouldReduceMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const [queryFocused, setQueryFocused] = useState(false);
  const [result, setResult] = useState<AiCheckResponse | null>(null);
  const [resultLocale, setResultLocale] = useState<string>("en");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Set true the moment the AI fetch resolves — drives the capybara
  // journey loader's final glide to the finish flag. Loader stays
  // mounted ~700 ms longer so the user actually sees the arrival.
  const [journeyDone, setJourneyDone] = useState(false);
  const [loaderMessageIndex, setLoaderMessageIndex] = useState(0);
  const [streamStatusMessage, setStreamStatusMessage] = useState<string | null>(
    null,
  );
  const [streamStage, setStreamStage] = useState<string | null>(null);
  const [reportStreamStarted, setReportStreamStarted] = useState(false);
  const latestRequested = useRef("");
  const searchFormRef = useRef<HTMLFormElement | null>(null);
  const fromListingId = useMemo(() => {
    const raw = searchParams.get("from")?.trim() ?? "";
    if (!raw) return null;
    const allowedPrefix =
      /^(?:iaai|copart(?:_(?:us|de|fi|es|uk))?|bca_eu|kvdcars|openlane_eu|auto1|autoscout24|otomoto|auksjonen|klaravik|autobid|autorola|exleasingcar|ayvens|troostwijk|alcopa)_[\w-]+$/i;
    return allowedPrefix.test(raw) ? raw : null;
  }, [searchParams]);
  const fromList = useMemo<"vehicles" | "notifications" | null>(() => {
    const raw = searchParams.get("fromList")?.trim();
    return raw === "vehicles" || raw === "notifications" ? raw : null;
  }, [searchParams]);
  // Set by list cards (Buy Now, /vehicles, Selected cars): the back CTA
  // returns to that list instead of a detail page the user never opened.
  const backTo = useMemo(
    () => parseBackToPath(searchParams.get("backTo")),
    [searchParams],
  );
  const backToDetailHref = fromListingId
    ? detailPathForListingId(
        fromListingId,
        fromList ? { from: fromList } : undefined,
      )
    : null;
  const freshBackToDetailHref = useMemo(() => {
    if (!backToDetailHref || !result) return backToDetailHref;
    const separator = backToDetailHref.includes("?") ? "&" : "?";
    return `${backToDetailHref}${separator}ai_check_updated=${Date.now()}`;
  }, [backToDetailHref, result]);
  const [remainingChecks, setRemainingChecks] = useState<number | null>(null);
  const [checksTier, setChecksTier] = useState<"free" | "pro" | "ultra" | null>(
    null,
  );
  const [nextCheckResetAt, setNextCheckResetAt] = useState<string | null>(null);
  const [showNoChecksModal, setShowNoChecksModal] = useState(false);
  // Confirmation gate for listings that need an explicit acknowledgement
  // before spending a check: limited visual data, potentially undamaged
  // sortation, or both. We stash the original runCheck arguments so the
  // Continue button can re-invoke the request with the right confirmations.
  const [pendingInsufficient, setPendingInsufficient] = useState<{
    raw: string;
    syncUrl: boolean;
    listingIdHint?: string;
    limitedImages: boolean;
    undamagedSortation: boolean;
  } | null>(null);
  // Distinct from `isLoading` (the long AI run): preflight is a quick
  // image-availability check that gates the modal. The submit button
  // shows a subtle spinner during this phase so the user knows we're
  // working — but the heavy "Analyzing…" journey loader is not mounted.
  const [isPreflightLoading, setIsPreflightLoading] = useState(false);

  /**
   * Closes the insufficient-image modal and sends the user back to where
   * they came from. The detail page (`/vehicles/...`) and grid cards
   * (`/buy-now`, `/selected-cars`) all set a `from` query param when
   * launching `/ai-check`, so the same priority order as the "Back to
   * car page" link in the result block applies:
   *   1. Real back-nav if browser history has prior entries (best UX —
   *      restores the user's scroll position on the source page).
   *   2. Otherwise push to the canonical detail path from `from`.
   *   3. Otherwise just close the modal (deep-link from outside the app,
   *      no source page to return to — let the user retype).
   */
  const cancelInsufficient = useCallback(() => {
    setPendingInsufficient(null);
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
      return;
    }
    if (backToDetailHref) {
      router.push(backToDetailHref);
    }
  }, [backToDetailHref, router]);

  const applyChecksBalance = useCallback((data: UserChecksBalance | null) => {
    if (!data) return;
    setRemainingChecks(
      typeof data.remaining === "number"
        ? data.remaining
        : (data.subscription ?? 0) + (data.purchased ?? 0) + (data.free ?? 0),
    );
    setChecksTier(
      data.tier === "pro" || data.tier === "ultra" ? data.tier : "free",
    );
    setNextCheckResetAt(
      typeof data.resetAt === "string" && data.resetAt.trim()
        ? data.resetAt
        : null,
    );
  }, []);

  const refreshChecksBalance = useCallback(async () => {
    try {
      const response = await fetch("/api/user/checks");
      applyChecksBalance(response.ok ? await response.json() : null);
    } catch {
      /* balance refresh is best-effort */
    }
  }, [applyChecksBalance]);

  // Fetch live check counts from the DB (session may be stale for up to 5 min).
  useEffect(() => {
    void refreshChecksBalance();
  }, [refreshChecksBalance]);

  const effectiveChecksTier =
    checksTier ??
    (session?.user?.tier === "pro" || session?.user?.tier === "ultra"
      ? session.user.tier
      : "free");
  const isFreeChecksUser = effectiveChecksTier === "free";
  const nextFreeCheckDate = useMemo(
    () => formatNextFreeCheckDate(nextCheckResetAt, locale),
    [locale, nextCheckResetAt],
  );
  const freeNoChecksBody = freeNoChecksBodyFor(nextCheckResetAt, locale, t);

  const { translated, isTranslating } = useTranslatedAi(
    result,
    resultLocale,
    locale,
  );
  const display = useMemo(() => {
    if (!result) return null;
    const merged = !translated
      ? result
      : {
          ...result,
          explain: translated.explain ?? result.explain,
          photo_damage_visible:
            translated.photo_damage_visible ?? result.photo_damage_visible,
          photo_vehicle_condition:
            translated.photo_vehicle_condition ??
            result.photo_vehicle_condition,
          repair_recommendations:
            translated.repair_recommendations ?? result.repair_recommendations,
          repair_items: translated.repair_items ?? result.repair_items,
          gallery_damage_by_image:
            translated.gallery_damage_by_image ??
            result.gallery_damage_by_image,
        };
    // Captions saved before the verdict still carry per-photo repair prices.
    if (result.repairability !== "parts_only") return merged;
    return {
      ...merged,
      gallery_damage_by_image: merged.gallery_damage_by_image?.map((item) => ({
        ...item,
        damage_description: stripRepairPriceMentions(item.damage_description),
      })),
    };
  }, [result, translated]);

  const loaderMessages = useMemo(
    () => [
      t("aiCheck.loadingListingSnapshot"),
      t("aiCheck.progress.weighingPriceTitleDamage"),
      t("aiCheck.progress.askingModelForScore"),
    ],
    [t],
  );

  // the lot's own photos for the bay's photo wall, as soon as the check has them
  const bayPhotoUrls = useBayPhotoUrls(
    result?.image_urls?.length
      ? result.image_urls
      : result?.ai_image_url
        ? [result.ai_image_url]
        : null,
  );
  const bayLabels = useMemo<InspectionBayLabels>(
    () => ({
      idleTitle: t("aiCheck.bay.idleTitle"),
      errorTitle: t("aiCheck.bay.errorTitle"),
      idleHint: t("aiCheck.bay.idleHint"),
      readyHint: t("aiCheck.bay.readyHint", { button: t("aiCheck.checkBtn") }),
      blockedTitle: t("aiCheck.bay.blockedTitle"),
      blockedHint: t("aiCheck.bay.blockedHint"),
      steps: {
        lot: t("aiCheck.bay.steps.lot"),
        photos: t("aiCheck.bay.steps.photos"),
        damage: t("aiCheck.bay.steps.damage"),
        report: t("aiCheck.bay.steps.report"),
      },
      lines: {
        hello: t("aiCheck.bay.lines.hello"),
        ready: t("aiCheck.bay.lines.ready"),
        found: t("aiCheck.bay.lines.found"),
        done: t("aiCheck.bay.lines.done"),
        oops: t("aiCheck.bay.lines.oops"),
      },
      sceneLabel: t("aiCheck.bay.sceneLabel"),
    }),
    [t],
  );

  // The bay keeps its cheer on screen for a moment after the report starts streaming in below it.
  const [bayCelebrating, setBayCelebrating] = useState(false);
  const reportWasStreaming = useRef(false);
  useEffect(() => {
    const streaming = isLoading && reportStreamStarted;
    if (streaming && !reportWasStreaming.current) {
      setBayCelebrating(true);
      window.setTimeout(() => setBayCelebrating(false), 1300);
    }
    reportWasStreaming.current = streaming;
  }, [isLoading, reportStreamStarted]);

  useEffect(() => {
    if (!isLoading) {
      setLoaderMessageIndex(0);
      return;
    }
    const iv = setInterval(
      () => setLoaderMessageIndex((c) => (c + 1) % loaderMessages.length),
      1400,
    );
    return () => clearInterval(iv);
  }, [isLoading, loaderMessages]);

  const runCheck = useCallback(
    async (
      raw: string,
      syncUrl: boolean,
      listingIdHint?: string,
      confirmInsufficient = false,
      confirmUndamaged = false,
      analysisKind?: "eu_buy_now",
      requestedCountry?: string,
    ) => {
      const q = raw.trim();
      const parsed = parseCarIdentifier(q);
      const resolvedListingId = listingIdHint ?? parsed?.listingId;
      const effectiveAnalysisKind =
        analysisKind ??
        (isEuBuyNowAiInput(q, parsed, resolvedListingId)
          ? "eu_buy_now"
          : undefined);
      if (syncUrl) {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (resolvedListingId) params.set("listing_id", resolvedListingId);
        if (effectiveAnalysisKind) {
          params.set("analysis_kind", effectiveAnalysisKind);
        }
        if (fromListingId) params.set("from", fromListingId);
        if (fromList) params.set("fromList", fromList);
        if (backTo) params.set("backTo", backTo);
        router.replace(
          params.size > 0 ? `/ai-check?${params.toString()}` : "/ai-check",
          { scroll: false },
        );
      }
      latestRequested.current = parsed?.query ?? q;
      setQuery(raw);
      setError("");
      setResult(null);
      // Clear any prior gate; if this run trips it again, we'll re-arm below.
      if (!confirmInsufficient && !confirmUndamaged) {
        setPendingInsufficient(null);
      }
      if (!q) {
        const message = t("aiCheck.errors.emptyInput");
        setError(message);
        toast({ tone: "error", message });
        return;
      }
      if (!parsed) {
        // A link to a site we DO read cars from, that simply names no car
        // ("https://www.iaai.com/"), gets the useful advice instead of being
        // told to paste the kind of link it already is.
        const message = isSupportedVehicleHost(q)
          ? t("shell.globalSearch.incompleteDescription")
          : t("aiCheck.errors.invalidInput");
        setError(message);
        toast({ tone: "error", message });
        return;
      }
      setIsLoading(true);
      setJourneyDone(false);
      setStreamStatusMessage(null);
      setStreamStage(null);
      setReportStreamStarted(false);
      // ── Preflight: ask the backend whether this listing has enough
      // visual data BEFORE starting the heavy AI run. If only a single
      // photo is available and no 360 spin can fill the gap, we want the
      // confirmation modal to appear before a credit is spent, but the
      // journey loader is already mounted so the button never feels stuck.
      // Skipped on the confirm-retry path (the user already saw and
      // dismissed the modal).
      if (
        !confirmInsufficient &&
        !confirmUndamaged &&
        effectiveAnalysisKind !== "eu_buy_now"
      ) {
        setIsPreflightLoading(true);
        try {
          const pre = await fetch("/api/ai-check/preflight", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              q: parsed.query,
              ...(resolvedListingId ? { listing_id: resolvedListingId } : {}),
              ...(parsed.source ? { listing_source: parsed.source } : {}),
              ...(effectiveAnalysisKind
                ? { analysis_kind: effectiveAnalysisKind }
                : {}),
            }),
          });
          if (pre.ok) {
            const preData = (await pre.json()) as {
              requires_confirmation?: boolean;
              requires_limited_images_confirmation?: boolean;
              requires_undamaged_confirmation?: boolean;
            };
            if (preData.requires_confirmation) {
              setIsPreflightLoading(false);
              const limitedImages =
                preData.requires_limited_images_confirmation === true;
              const undamagedSortation =
                preData.requires_undamaged_confirmation === true;
              setPendingInsufficient({
                raw,
                syncUrl,
                listingIdHint,
                limitedImages:
                  limitedImages ||
                  (!undamagedSortation &&
                    preData.requires_confirmation === true),
                undamagedSortation,
              });
              setIsLoading(false);
              setJourneyDone(false);
              setStreamStatusMessage(null);
              setStreamStage(null);
              setReportStreamStarted(false);
              return;
            }
          }
          // Non-OK preflight is non-fatal — the main route still has its
          // own gate as a safety net. Fall through to the full analysis.
        } catch {
          /* preflight is best-effort; ignore network errors */
        } finally {
          setIsPreflightLoading(false);
        }
      }
      try {
        const analyticsTier =
          session?.user?.tier === "ultra"
            ? "ultra"
            : session?.user?.tier === "pro"
              ? "pro"
              : "free";
        const analyticsSource =
          parsed.source ?? effectiveAnalysisKind ?? undefined;
        track("ai_check_started", {
          tier: analyticsTier,
          source: analyticsSource,
        });
        const res = await fetch("/api/ai-check?stream=1", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            q: parsed.query,
            locale,
            country: requestedCountry ?? country,
            ...(resolvedListingId ? { listing_id: resolvedListingId } : {}),
            ...(parsed.source ? { listing_source: parsed.source } : {}),
            ...(effectiveAnalysisKind
              ? { analysis_kind: effectiveAnalysisKind }
              : {}),
            ...(confirmInsufficient ? { confirm_insufficient: true } : {}),
            ...(confirmUndamaged ? { confirm_undamaged: true } : {}),
          }),
        });

        if (!res.body) {
          throw new Error("Missing response stream");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let terminalHandled = false;

        const handleErrorPayload = (
          status: number,
          data: {
            error?: string;
            detail?: string;
            requires_confirmation?: boolean;
            requires_limited_images_confirmation?: boolean;
            requires_undamaged_confirmation?: boolean;
            single_image?: boolean;
          },
        ) => {
          setResult(null);
          if (status === 402) {
            setRemainingChecks(0);
            void refreshChecksBalance();
            setShowNoChecksModal(true);
            return true;
          }
          // 409 + requires_confirmation: backend detected only one usable
          // image (no 360 frames) and refuses to burn a check without the
          // user explicitly opting in. Open the confirmation modal so they
          // can decide whether to proceed with reduced accuracy.
          if (status === 409 && data.requires_confirmation) {
            const limitedImages =
              data.requires_limited_images_confirmation === true ||
              data.single_image === true;
            setPendingInsufficient({
              raw,
              syncUrl,
              listingIdHint,
              limitedImages,
              undamagedSortation: data.requires_undamaged_confirmation === true,
            });
            return true;
          }
          const message = data.detail
            ? `${data.error ?? "Request failed"}: ${data.detail}`
            : (data.error ?? "Unable to analyze this listing right now.");
          setError(message);
          toast({ tone: "error", message });
          return true;
        };

        const handleSuccessPayload = (data: AiCheckResponse) => {
          if (typeof data.remaining === "number") {
            setRemainingChecks(data.remaining);
          }
          setResult(data);
          track("ai_check_completed", {
            tier: analyticsTier,
            source: analyticsSource,
          });
          // Remember the finished report so returning to /ai-check with the
          // same `?q=` paints it instantly (see the mount effect below).
          AI_CHECK_RESULT_CACHE.set(aiCheckCacheKey(q), {
            result: data,
            locale: data.generated_locale ?? "en",
          });
          markAiCheckUpdatedListing(data.listing_id);
          if (resolvedListingId && resolvedListingId !== data.listing_id) {
            markAiCheckUpdatedListing(resolvedListingId);
          }
          // The backend always generates the AI response in English. The
          // `generated_locale` field is forwarded from the server for clarity
          // and future flexibility, but defaults to "en".
          setResultLocale(data.generated_locale ?? "en");
        };

        const handleStreamLine = (line: string) => {
          const event = JSON.parse(line) as AiCheckStreamEvent;
          if (event.type === "status") {
            setStreamStage(event.stage);
            setStreamStatusMessage(event.message ?? null);
            return;
          }
          if (event.type === "partial") {
            setStreamStage(event.stage);
            if (
              event.stage === "summary" ||
              event.stage === "repairs" ||
              event.stage === "price" ||
              event.stage === "gallery" ||
              event.stage === "cache" ||
              event.stage === "eu-analysis"
            ) {
              setReportStreamStarted(true);
            }
            setResult((current) => mergeAiCheckPatch(current, event.patch));
            if (typeof event.patch.generated_locale === "string") {
              setResultLocale(event.patch.generated_locale);
            }
            return;
          }
          if (event.type === "done") {
            terminalHandled = true;
            setStreamStage("done");
            setReportStreamStarted(true);
            handleSuccessPayload(event.payload);
            return;
          }
          if (event.type === "error") {
            terminalHandled = true;
            setStreamStage("error");
            handleErrorPayload(event.status, event.payload);
          }
        };

        while (true) {
          const { value, done } = await reader.read();
          buffer += decoder.decode(value ?? new Uint8Array(), {
            stream: !done,
          });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (line.trim()) handleStreamLine(line);
          }
          if (done) break;
        }

        if (buffer.trim()) {
          handleStreamLine(buffer);
        }

        if (!terminalHandled) {
          throw new Error("Stream closed before completion");
        }
      } catch {
        const message = t("aiCheck.errors.network");
        setError(message);
        toast({ tone: "error", message });
      } finally {
        // Let the inspection bay play its last beat (Carpy's cheer, or
        // his sad face on a failed check), then settle the run 700 ms
        // later.
        setJourneyDone(true);
        setTimeout(() => {
          setIsLoading(false);
          setJourneyDone(false);
          setStreamStatusMessage(null);
          setStreamStage(null);
        }, 700);
      }
    },
    [
      backTo,
      country,
      fromList,
      fromListingId,
      locale,
      refreshChecksBalance,
      router,
      session?.user?.tier,
      t,
      toast,
    ],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q")?.trim() ?? "";
    const listingIdHint = params.get("listing_id")?.trim() || undefined;
    const analysisKind =
      params.get("analysis_kind") === "eu_buy_now" ? "eu_buy_now" : undefined;
    const countryHint =
      params.get("country")?.trim().toUpperCase() || undefined;
    if (q) setQuery(q);

    // Instant restore: if this exact query was already analyzed this session,
    // paint the finished report immediately instead of re-running (and showing
    // the journey loader). Overrides autorun — a re-run would only reproduce the
    // same server-cached result. The user can still re-analyze via the form.
    if (q) {
      const cached = AI_CHECK_RESULT_CACHE.get(aiCheckCacheKey(q));
      if (cached) {
        latestRequested.current = q;
        setResult(cached.result);
        setResultLocale(cached.locale);
        setIsLoading(false);
        setReportStreamStarted(true);
        setJourneyDone(true);
        setStreamStage("done");
        if (params.get("autorun") === "1") {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete("autorun");
          cleanUrl.searchParams.delete("listing_id");
          cleanUrl.searchParams.delete("analysis_kind");
          window.history.replaceState(null, "", cleanUrl.toString());
        }
        return;
      }
    }

    // When a caller links here with `autorun=1` (e.g. the "Analyse
    // Damage" button on a /vehicles card), kick off the check
    // immediately and strip the flag from the URL so a refresh doesn't
    // spend another credit. `listing_id` is also stripped — it's a
    // first-request-only routing hint (EU auctions → load via catalog
    // collection; omitting it on retry falls back to the default flow).
    const shouldAutorun = params.get("autorun") === "1";
    if (shouldAutorun && q) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("autorun");
      cleanUrl.searchParams.delete("listing_id");
      cleanUrl.searchParams.delete("analysis_kind");
      window.history.replaceState(null, "", cleanUrl.toString());
      void runCheck(
        q,
        false,
        listingIdHint,
        false,
        false,
        analysisKind,
        countryHint,
      );
      // Phone-landscape deep-link: the hero pushes the prefilled input +
      // journey loader below the fold, so the page looks idle. Jump to
      // the form so the field and progress are visible. Same gating as
      // /vincheck — coarse pointer, landscape, short viewport only.
      if (
        window.matchMedia(
          "(pointer: coarse) and (orientation: landscape) and (max-height: 500px)",
        ).matches
      ) {
        // Double rAF: wait for the loader to mount + paint before measuring.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            searchFormRef.current?.scrollIntoView({
              behavior: "auto",
              block: "start",
            });
          });
        });
      }
    }
    // Only on first mount — runCheck identity is stable after init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (remainingChecks === 0) {
      setShowNoChecksModal(true);
      return;
    }
    void runCheck(query, true);
  };

  const pendingConfirmationTitle = pendingInsufficient
    ? pendingInsufficient.limitedImages &&
      pendingInsufficient.undamagedSortation
      ? t("aiCheck.undamagedLimitedImages.title")
      : pendingInsufficient.undamagedSortation
        ? t("aiCheck.undamagedVehicle.title")
        : t("aiCheck.insufficientImages.title")
    : "";
  const pendingConfirmationBody = pendingInsufficient
    ? pendingInsufficient.limitedImages &&
      pendingInsufficient.undamagedSortation
      ? t("aiCheck.undamagedLimitedImages.body")
      : pendingInsufficient.undamagedSortation
        ? t("aiCheck.undamagedVehicle.body")
        : t("aiCheck.insufficientImages.body")
    : "";

  const handleExportPdf = useCallback(async () => {
    if (!result || !display) return;
    return import("../../lib/pdf-export").then(({ exportAiPdf }) =>
      exportAiPdf(
        {
          listing_title: result.listing_title,
          listing_vin: result.listing_vin,
          listing_url: result.listing_url,
          explain: display.explain,
          photo_damage_visible: display.photo_damage_visible,
          photo_vehicle_condition:
            display.photo_vehicle_condition,
          repair_recommendations: display.repair_recommendations,
          repair_analysis_id: result.repair_analysis_id,
          repair_items: display.repair_items,
          repair_total_min_usd: result.repair_total_min_usd,
          repair_total_max_usd: result.repair_total_max_usd,
          gallery_damage_by_image:
            display.gallery_damage_by_image,
          repairability: result.repairability,
          price_verdict: result.price_verdict,
          buy_now_price_usd: result.buy_now_price_usd,
          current_bid_usd: result.current_bid_usd,
          buy_now_price_coefficient:
            result.buy_now_price_coefficient,
          vin_history: result.vin_history,
          is_flip: result.is_flip,
          flip_warning: result.flip_warning,
          bidcars_sales_history: result.bidcars_sales_history,
        },
        t,
        locale,
      ),
    );
  }, [display, locale, result, t]);

  const isEuAnalysis = result?.analysis_kind === "eu_buy_now";
  const hasEuAnalysis = Boolean(result?.eu_analysis);
  const hasAiNarrative = Boolean(
    display &&
    !isEuAnalysis &&
    (hasCopy(display.photo_damage_visible) ||
      hasCopy(display.photo_vehicle_condition) ||
      hasCopy(display.explain)),
  );
  const hasRepairDetails = Boolean(
    display &&
    !isEuAnalysis &&
    ((display.repair_items?.length ?? 0) > 0 ||
      hasCopy(display.repair_recommendations)),
  );
  const hasPriceVerdict = Boolean(!isEuAnalysis && result?.price_verdict);
  const hasGalleryAnalysis = Boolean(
    !isEuAnalysis && (display?.gallery_damage_by_image?.length ?? 0) > 0,
  );
  const showAiReport = Boolean(
    display &&
    !isEuAnalysis &&
    (hasAiNarrative || hasRepairDetails || hasPriceVerdict || !isLoading),
  );
  const showRepairSection = Boolean(
    display &&
    !isEuAnalysis &&
    (hasRepairDetails || hasPriceVerdict || !isLoading),
  );
  const showGallerySection = Boolean(
    display && result && !isEuAnalysis && (hasGalleryAnalysis || !isLoading),
  );
  const hasReportContent =
    hasGalleryAnalysis || hasAiNarrative || hasRepairDetails || hasPriceVerdict;
  const useProgressiveReport = Boolean(
    display &&
    result &&
    hasReportContent &&
    (reportStreamStarted || !isLoading),
  );
  const showStandardAiReport = showAiReport && !useProgressiveReport;
  const showStandardGallerySection =
    showGallerySection && !useProgressiveReport;
  const showJourneyLoader = isLoading && !reportStreamStarted;
  // One panel for the whole life of a check (see InspectionBayPanel): the bay before Analyze, the stages while it
  // runs, a short cheer once the report streams in, and Carpy's sad face when a check fails.
  const bayMode: InspectionBayMode | null = showJourneyLoader
    ? streamStage === "error" || (journeyDone && error)
      ? "error"
      : journeyDone
        ? "done"
        : "loading"
    : bayCelebrating
      ? "done"
      : !result && !isLoading
        ? error
          ? "error"
          : "idle"
        : null;
  const showProgressPanel = Boolean(
    isLoading && result && !reportStreamStarted,
  );
  const hasSearchInput = query.trim().length > 0;
  const bayInputValid = hasSearchInput && parseCarIdentifier(query.trim()) !== null;

  return (
    <div className="flex flex-col gap-8">
      <section className="relative overflow-hidden bg-(--bg-card) border border-(--border) rounded-2xl p-6 md:p-8">
        <AiCheckHeroBackdrop />
        <div className="relative z-10 flex flex-col items-center gap-4 text-center md:flex-row md:items-start md:text-left">
          <div className="anim-scale-in shrink-0">
            <AiScanCapybaraScene size={112} />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-(--text-primary) inline-flex items-center gap-3">
              {t("aiCheck.title")}
              <span className="text-xs font-bold uppercase leading-none px-2 py-1 rounded-lg bg-(--status-special-bg) text-(--status-special-text) border border-(--status-special-border)">
                AI
              </span>
            </h1>
            <p className="mt-2 max-w-2xl text-(--text-muted)">
              {t(
                country === "UA" ? "aiCheck.subtitle" : "aiCheck.subtitleNoRia",
              )}
            </p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-(--border) bg-(--bg-tertiary) px-3 py-1.5 text-xs">
              {remainingChecks === null ? (
                <>
                  <div className="h-4 w-4 rounded-full bg-(--border) animate-pulse" />
                  <div className="h-4 w-32 rounded bg-(--border) animate-pulse" />
                </>
              ) : (
                <>
                  <m.span
                    className="inline-flex"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : remainingChecks > 0
                          ? { scale: [1, 1.18, 1] }
                          : { rotate: [-8, 8, -8] }
                    }
                    transition={{
                      duration: remainingChecks > 0 ? 2.4 : 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Zap
                      className={`w-3.5 h-3.5 ${remainingChecks > 0 ? "text-(--accent)" : "text-(--status-danger)"}`}
                    />
                  </m.span>
                  <span className="text-(--text-muted)">
                    <span
                      className={`font-bold tabular-nums ${remainingChecks > 0 ? "text-(--text-primary)" : "text-(--status-danger)"}`}
                    >
                      {remainingChecks}
                    </span>{" "}
                    {remainingChecks === 1
                      ? t("aiCheck.checkRemaining")
                      : t("aiCheck.checksRemaining")}
                  </span>
                  {remainingChecks === 0 && isFreeChecksUser ? (
                    <span className="ml-1 text-(--text-faint)">
                      {nextFreeCheckDate
                        ? t("aiCheck.nextFreeCheck", {
                            date: nextFreeCheckDate,
                          })
                        : t("aiCheck.nextFreeCheckMonthly")}
                    </span>
                  ) : remainingChecks === 0 ? (
                    <Link
                      href="/buy-checks"
                      className="ml-1 font-semibold text-(--accent) hover:underline"
                    >
                      {t("aiCheck.buyMore")}
                    </Link>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <form
          ref={searchFormRef}
          onSubmit={handleSubmit}
          className="relative z-10 mt-6 flex flex-col gap-3 scroll-mt-[calc(var(--app-safe-top,0px)+4rem)] md:flex-row md:items-stretch"
        >
          <div className="group relative flex-1">
            <span
              aria-hidden
              className={`absolute left-3 top-1/2 -translate-y-1/2 inline-flex transition-colors ${
                queryFocused ? "text-(--accent)" : "text-(--text-muted)"
              }`}
            >
              <m.span
                className="inline-flex"
                animate={{
                  scale: queryFocused ? 1.18 : 1,
                  rotate: queryFocused ? -8 : 0,
                }}
                transition={{ type: "spring", stiffness: 380, damping: 22 }}
              >
                <Search className="w-5 h-5" />
              </m.span>
            </span>
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setError("");
              }}
              onFocus={() => setQueryFocused(true)}
              onBlur={() => setQueryFocused(false)}
              placeholder={t(
                country === "UA"
                  ? "aiCheck.placeholder"
                  : "aiCheck.placeholderNoRia",
              )}
              autoComplete="off"
              aria-label={t(
                country === "UA"
                  ? "aiCheck.placeholder"
                  : "aiCheck.placeholderNoRia",
              )}
              className="w-full h-12 pl-11 pr-4 bg-(--bg-tertiary) border border-(--border) rounded-xl text-(--text-primary) placeholder:text-(--text-muted) transition-[border-color,box-shadow] group-hover:border-(--accent)/45"
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={
              isLoading ||
              isPreflightLoading ||
              !!pendingInsufficient ||
              !remainingChecks ||
              !hasSearchInput
            }
            aria-label={t("aiCheck.checkBtn")}
            className="shrink-0 px-4 sm:px-8 rounded-lg hover:scale-[1.02]"
          >
            {isLoading || isPreflightLoading
              ? t("aiCheck.analyzing")
              : t("aiCheck.checkBtn")}
          </Button>
        </form>

        {/* While the inspection bay is on screen it shows the error in its own fixed status box (with Carpy's sad
            face), so the hero never grows; with a report below, the error stays under the search box. */}
        <AnimatePresence>
          {error && !bayMode ? (
            <m.p
              key="ai-error"
              className="relative z-10 mt-3 text-sm font-medium text-(--status-danger-text)"
              initial={shouldReduceMotion ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={shouldReduceMotion ? undefined : { opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              {error}
            </m.p>
          ) : null}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {bayMode ? (
            <m.div
              key="inspection-bay"
              className="relative z-10 overflow-hidden"
              initial={shouldReduceMotion ? false : { opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={shouldReduceMotion ? undefined : { opacity: 0, height: 0 }}
              transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
            >
              <InspectionBayPanel
                className="mt-4"
                mode={bayMode}
                stage={streamStage}
                message={streamStatusMessage ?? loaderMessages[loaderMessageIndex]}
                subtitle={t("aiCheck.loaderSubtitle")}
                errorText={error}
                focused={queryFocused}
                hasInput={hasSearchInput}
                valid={bayInputValid}
                blocked={remainingChecks === 0}
                photos={result?.image_urls?.length ?? 0}
                photoUrls={bayPhotoUrls}
                labels={bayLabels}
              />
            </m.div>
          ) : null}
        </AnimatePresence>
      </section>

      {result && display ? (
        <section className="relative space-y-5">
          {/* Publishes this analysis to the Carpy chat assistant (read via
              readPageActionContext) so it can answer buy/skip questions using
              the real verdict on this page instead of asking for the car again. */}
          <div
            hidden
            data-chat-page="vehicle-detail"
            data-chat-listing-id={result.listing_id ?? undefined}
            data-chat-title={result.listing_title ?? undefined}
            data-chat-vin={result.listing_vin ?? undefined}
            data-chat-auction-url={result.listing_url ?? undefined}
            data-chat-ai-analysis-ready="1"
            data-chat-ai-analysis-href={
              result.listing_id
                ? detailPathForListingId(result.listing_id)
                : undefined
            }
            data-chat-ai-analysis-kind={
              isEuAnalysis ? "eu_buy_now" : "us_damage"
            }
            data-chat-analysis-summary={buildAiCheckChatSummary(result)}
          />
          {!isLoading && !isEuAnalysis && session?.user?.tier !== "free" ? (
            <div className="flex justify-end mb--3">
              <ExportPdfButton
                disabled={isTranslating}
                onExport={handleExportPdf}
              />
            </div>
          ) : null}

          <ScrollReveal>
            <div className="space-y-5 rounded-2xl border border-(--border) bg-(--bg-card) p-5">
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-(--text-primary) wrap-break-word">
                    {result.listing_title ?? "—"}
                  </h2>
                  {result.listing_vin && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <p className="text-xs font-mono text-(--text-muted)">
                        {t("vehicles.card.vin", {
                          status: result.listing_vin,
                        })}
                      </p>
                      {result.listing_vin &&
                        !result.listing_vin.includes("*") && (
                          <VinCheckLink
                            vin={result.listing_vin}
                            variant="secondary"
                            label={t("aiCheck.vinCheck")}
                            from={fromListingId}
                          />
                        )}
                    </div>
                  )}
                </div>
                {session?.user?.tier === "free" ? (
                  <div className="pointer-events-none order-first inline-flex max-w-full shrink-0 self-start rounded-full border border-(--accent)/35 bg-(--bg-card)/90 px-3 py-1 text-center text-[10px] font-bold uppercase leading-tight tracking-widest text-(--accent) backdrop-blur-sm sm:order-none">
                    Free monthly report
                  </div>
                ) : null}
              </div>
              <VehicleQuickActions
                backHref={backTo ?? freshBackToDetailHref}
                backLabel={
                  backTo
                    ? t(backToListLabelKey(backTo))
                    : t("aiCheck.backToCarPage")
                }
                // Only a list card sets `backTo`, and this page only ever
                // replaces its URL, so the entry behind it is that list:
                // history back restores its scroll. A cold load (new tab,
                // no history) follows the href instead.
                useHistoryBack={Boolean(backTo)}
                listingUrl={result.listing_url}
                viewAiCheckedLabel={t("aiCheck.viewAiCheckedCars")}
                openListingLabel={t("aiCheck.openListing")}
              />
            </div>
          </ScrollReveal>

          {showProgressPanel ? (
            <AiCheckStreamingProgress
              result={result}
              stage={streamStage}
              message={streamStatusMessage}
              shouldReduceMotion={shouldReduceMotion}
            />
          ) : null}

          {hasEuAnalysis && result.eu_analysis ? (
            <ProgressiveEuAiReport
              key={`${result.listing_id ?? result.listing_url}-eu-progressive-report`}
              analysis={result.eu_analysis}
              generatedLocale={resultLocale}
              imageUrls={
                result.image_urls?.length
                  ? result.image_urls
                  : result.ai_image_url
                    ? [result.ai_image_url]
                    : []
              }
              listingTitle={result.listing_title}
              reportKey={`${result.listing_id ?? result.listing_url}`}
            />
          ) : null}

          {/* Limited-image-data warning banner — analysis ran on a single
              photo because the listing had no 360 spin to fall back on. */}
          {!isEuAnalysis && result.single_image_analysis && (
            <m.div
              className="rounded-2xl border border-(--status-warning-border) bg-(--status-warning-bg) p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              initial={shouldReduceMotion ? false : { opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              role="status"
            >
              <div className="flex items-center gap-2 flex-1">
                <m.span
                  className="inline-flex"
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { rotate: [-6, 6, -6], scale: [1, 1.1, 1] }
                  }
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-(--status-warning)" />
                </m.span>
                <div>
                  <span className="text-sm font-bold text-(--status-warning-text)">
                    {t("aiCheck.singleImageBanner.title")}
                  </span>
                  <p className="mt-0.5 text-xs text-(--text-muted)">
                    {t("aiCheck.singleImageBanner.body")}
                  </p>
                </div>
              </div>
            </m.div>
          )}

          {/* Auction history warning banner */}
          {!isEuAnalysis && result.vin_history && (
            <m.div
              className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                result.vin_history.risk_level === "high"
                  ? "border-(--status-danger-border) bg-(--status-danger-bg)"
                  : result.vin_history.risk_level === "medium"
                    ? "border-(--status-warning-border) bg-(--status-warning-bg)"
                    : "border-(--status-info-border) bg-(--status-info-bg)"
              }`}
              initial={shouldReduceMotion ? false : { opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <m.span
                    className="inline-flex"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : { rotate: [-6, 6, -6], scale: [1, 1.1, 1] }
                    }
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <AlertTriangle
                      className={`h-4 w-4 shrink-0 ${
                        result.vin_history.risk_level === "high"
                          ? "text-(--status-danger)"
                          : result.vin_history.risk_level === "medium"
                            ? "text-(--status-warning)"
                            : "text-(--status-info)"
                      }`}
                    />
                  </m.span>
                  <span
                    className={`text-sm font-bold ${
                      result.vin_history.risk_level === "high"
                        ? "text-(--status-danger-text)"
                        : result.vin_history.risk_level === "medium"
                          ? "text-(--status-warning-text)"
                          : "text-(--status-info-text)"
                    }`}
                  >
                    {result.vin_history.risk_level === "high"
                      ? t("aiCheck.vinHistory.highRisk")
                      : result.vin_history.risk_level === "medium"
                        ? t("aiCheck.vinHistory.mediumRisk")
                        : t("aiCheck.vinHistory.found")}{" "}
                    {t("aiCheck.vinHistory.listedAtAuction", {
                      count: result.vin_history.sale_count,
                    })}
                  </span>
                </div>
                <p className="mt-1 text-xs text-(--text-muted)">
                  {result.vin_history.damage_escalation
                    ? t("aiCheck.vinHistory.damageEscalated")
                    : t("aiCheck.vinHistory.previousDamages", {
                        value:
                          result.vin_history.previous_damages?.join(", ") ||
                          t("aiCheck.vinHistory.unknown"),
                      })}
                  {result.vin_history.price_adjustment_factor < 1
                    ? ` ${t("aiCheck.vinHistory.pricesReduced", {
                        pct: Math.round(
                          (1 - result.vin_history.price_adjustment_factor) *
                            100,
                        ),
                      })}`
                    : ""}
                </p>
              </div>
              {result.listing_vin && !result.listing_vin.includes("*") && (
                <VinCheckLink
                  vin={result.listing_vin}
                  variant="secondary"
                  label={t("aiCheck.vinHistory.viewFullHistory")}
                  from={fromListingId}
                />
              )}
            </m.div>
          )}

          {/* Flip warning from bid.cars */}
          {!isEuAnalysis &&
            Boolean((result as Record<string, unknown>).is_flip) && (
              <m.div
                className="rounded-2xl border border-(--status-danger-border) bg-(--status-danger-bg) p-4 space-y-2"
                initial={shouldReduceMotion ? false : { opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.05,
                }}
              >
                <div className="flex items-center gap-2">
                  <m.span
                    className="inline-flex"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : { rotate: [-8, 8, -8], scale: [1, 1.1, 1] }
                    }
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 text-(--status-danger)" />
                  </m.span>
                  <span className="text-sm font-bold text-(--status-danger-text)">
                    {t("aiCheck.flipWarning.title")}
                  </span>
                </div>
                <p className="text-xs text-(--status-danger-text)/90 leading-relaxed">
                  <span className="font-semibold text-(--status-danger-text)">
                    {t("aiCheck.flipWarning.whatTitle")}
                  </span>{" "}
                  {t("aiCheck.flipWarning.whatBody")}
                </p>
                <p className="text-xs text-(--status-danger-text)/90 leading-relaxed">
                  <span className="font-semibold text-(--status-danger-text)">
                    {t("aiCheck.flipWarning.commonTitle")}
                  </span>{" "}
                  {t("aiCheck.flipWarning.commonLead")}{" "}
                  <span className="font-semibold">
                    {t("aiCheck.flipWarning.commonStat")}
                  </span>{" "}
                  {t("aiCheck.flipWarning.commonTail")}
                </p>
                <p className="text-xs text-(--status-danger-text)/90 leading-relaxed">
                  <span className="font-semibold text-(--status-danger-text)">
                    {t("aiCheck.flipWarning.riskyTitle")}
                  </span>
                </p>
                <ul className="text-xs text-(--status-danger-text)/90 leading-relaxed list-disc pl-5 space-y-0.5">
                  <li>
                    <span className="font-semibold">
                      {t("aiCheck.flipWarning.hiddenStructuralTitle")}
                    </span>{" "}
                    {t("aiCheck.flipWarning.hiddenStructuralBody")}
                  </li>
                  <li>
                    <span className="font-semibold">
                      {t("aiCheck.flipWarning.salvageTitle")}
                    </span>{" "}
                    {t("aiCheck.flipWarning.salvageBody")}
                  </li>
                  <li>
                    <span className="font-semibold">
                      {t("aiCheck.flipWarning.premiumTitle")}
                    </span>{" "}
                    {t("aiCheck.flipWarning.premiumBody")}
                  </li>
                  <li>
                    <span className="font-semibold">
                      {t("aiCheck.flipWarning.reliabilityTitle")}
                    </span>{" "}
                    {t("aiCheck.flipWarning.reliabilityBody")}
                  </li>
                </ul>
                <p className="text-xs text-(--status-danger-text)/90 leading-relaxed pt-1 border-t border-(--status-danger-border)">
                  {String(
                    (result as Record<string, unknown>).flip_warning ||
                      t("aiCheck.flipWarning.fallback"),
                  )}
                </p>
              </m.div>
            )}

          {/* Non-verified seller warning — mirror of the flip box, keyed off a
              non-insurance seller in the listing / auction history. */}
          {!isEuAnalysis &&
            Boolean(
              (result as Record<string, unknown>).is_non_insurance_seller,
            ) && (
              <m.div
                className="rounded-2xl border border-(--status-warning-border) bg-(--status-warning-bg) p-4 space-y-2"
                initial={shouldReduceMotion ? false : { opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.05,
                }}
              >
                <div className="flex items-center gap-2">
                  <m.span
                    className="inline-flex"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : { rotate: [-8, 8, -8], scale: [1, 1.1, 1] }
                    }
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 text-(--status-warning)" />
                  </m.span>
                  <span className="text-sm font-bold text-(--status-warning-text)">
                    {t("aiCheck.sellerWarning.title")}
                  </span>
                </div>
                <p className="text-xs text-(--status-warning-text)/90 leading-relaxed">
                  {t("aiCheck.sellerWarning.body", {
                    seller:
                      String(
                        (result as Record<string, unknown>)
                          .non_insurance_seller_name || "",
                      ).trim() || t("aiCheck.sellerWarning.genericSeller"),
                  })}
                </p>
                <p className="text-xs text-(--status-warning-text)/90 leading-relaxed">
                  <span className="font-semibold text-(--status-warning-text)">
                    {t("aiCheck.sellerWarning.whyTitle")}
                  </span>
                </p>
                <ul className="text-xs text-(--status-warning-text)/90 leading-relaxed list-disc pl-5 space-y-0.5">
                  <li>{t("aiCheck.sellerWarning.point1")}</li>
                  <li>{t("aiCheck.sellerWarning.point2")}</li>
                  <li>{t("aiCheck.sellerWarning.point3")}</li>
                </ul>
              </m.div>
            )}

          {/* Sales history inline */}
          {!isEuAnalysis &&
            Array.isArray(
              (result as Record<string, unknown>).bidcars_sales_history,
            ) &&
            (
              (result as Record<string, unknown>)
                .bidcars_sales_history as Array<Record<string, unknown>>
            ).length > 0 && (
              <ScrollReveal>
                <AuctionSalesHistory
                  rows={
                    (result as Record<string, unknown>)
                      .bidcars_sales_history as AuctionSaleRecord[]
                  }
                  labels={{
                    title: t("aiCheck.auctionHistory"),
                    records: t("aiCheck.auctionHistoryRecords"),
                    lot: t("aiCheck.vinHistory.lot"),
                    auction: t("vin.auctionHistory.tableHeaders.auction"),
                    date: t("vin.auctionHistory.tableHeaders.date"),
                    finalBid: t("vin.auctionHistory.tableHeaders.finalBid"),
                    odometer: t("vin.auctionHistory.tableHeaders.odometer"),
                    status: t("vin.auctionHistory.tableHeaders.status"),
                    seller: t("vin.auctionHistory.tableHeaders.seller"),
                  }}
                  locale={locale}
                  currentLotNumber={result.listing_stock_number}
                  headerAction={
                    result.listing_vin && !result.listing_vin.includes("*") ? (
                      <VinCheckLink
                        vin={result.listing_vin}
                        variant="primary"
                        label={t("aiCheck.vinCheck")}
                        from={fromListingId}
                      />
                    ) : null
                  }
                />
              </ScrollReveal>
            )}

          {useProgressiveReport ? (
            <ProgressiveAiReport
              key={`${result.listing_id ?? result.listing_url}-progressive-report`}
              display={display as AiCheckResponse}
              result={result}
            />
          ) : null}

          {showStandardAiReport ? (
            <m.div
              className="rounded-2xl border border-(--border) bg-(--bg-secondary) px-4 py-4 text-sm"
              initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.08,
              }}
            >
              <div className="inline-flex items-center gap-2 font-semibold text-(--text-primary)">
                <m.span
                  className="inline-flex"
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { rotate: [0, 12, -12, 0], scale: [1, 1.15, 1] }
                  }
                  transition={{
                    duration: 2.6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <Sparkles
                    className="h-4 w-4 shrink-0 text-(--accent)"
                    aria-hidden
                  />
                </m.span>
                {t("aiCheck.aiExplanation")}
              </div>
              <div className="mt-4 space-y-4">
                {hasAiNarrative || !isLoading ? (
                  <>
                    <m.div
                      initial={
                        shouldReduceMotion ? false : { opacity: 0, y: 8 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                    >
                      <h3 className="text-xs font-bold uppercase tracking-wide text-(--text-muted)">
                        {t("aiCheck.damagePhoto")}
                      </h3>
                      <p className="mt-1.5 leading-relaxed text-(--text-secondary)">
                        {display.photo_damage_visible || "—"}
                        {isTranslating && (
                          <span className="ml-1 text-xs text-(--text-faint) animate-pulse">
                            …
                          </span>
                        )}
                      </p>
                    </m.div>
                    <m.div
                      initial={
                        shouldReduceMotion ? false : { opacity: 0, y: 8 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.28,
                        ease: "easeOut",
                        delay: 0.06,
                      }}
                    >
                      <h3 className="text-xs font-bold uppercase tracking-wide text-(--text-muted)">
                        {t("aiCheck.conditionPhoto")}
                      </h3>
                      <p className="mt-1.5 leading-relaxed text-(--text-secondary)">
                        {display.photo_vehicle_condition || "—"}
                      </p>
                    </m.div>
                    <m.div
                      className="border-t border-(--border) pt-4"
                      initial={
                        shouldReduceMotion ? false : { opacity: 0, y: 8 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.28,
                        ease: "easeOut",
                        delay: 0.12,
                      }}
                    >
                      <h3 className="text-xs font-bold uppercase tracking-wide text-(--text-muted)">
                        {t("aiCheck.narrativeSummary")}
                      </h3>
                      <p className="mt-1.5 leading-relaxed text-(--text-muted)">
                        {display.explain || t("aiCheck.noExplanation")}
                      </p>
                    </m.div>
                  </>
                ) : null}
                {showRepairSection ? (
                  <m.div
                    className="border-t border-(--border) pt-4"
                    initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut", delay: 0.18 }}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-(--text-muted)">
                        {t("aiCheck.repairRecommendations")}
                      </h3>
                      {display.repair_items?.length ? <RepairReportExportButton disabled={isTranslating} data={{
                        listing_title: result.listing_title, listing_vin: result.listing_vin, listing_url: result.listing_url,
                        repair_analysis_id: result.repair_analysis_id, repair_items: display.repair_items,
                        repair_total_min_usd: result.repair_total_min_usd, repair_total_max_usd: result.repair_total_max_usd,
                      }} /> : null}
                    </div>
                    {display.repair_items && display.repair_items.length > 0 ? (
                      <div className="mt-2 grid gap-2 sm:grid-cols-1 lg:grid-cols-2">
                        {display.repair_items.map((item, index) => (
                          <m.div
                            key={`${item.part}-${index}`}
                            className="rounded-lg border border-(--border) bg-(--bg-card) px-3 py-2 transition-colors hover:border-(--accent)/35"
                            initial={
                              shouldReduceMotion ? false : { opacity: 0, y: 8 }
                            }
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 0.32,
                              ease: "easeOut",
                              delay: shouldReduceMotion
                                ? 0
                                : 0.05 + index * 0.05,
                            }}
                          >
                            <p className="text-sm font-semibold text-(--text-primary)">
                              {item.part} -{" "}
                              {item.action === "replace"
                                ? t("aiCheck.actionReplace")
                                : t("aiCheck.actionRepair")}
                            </p>
                            <p className="text-sm text-(--text-secondary)">
                              ${item.estimated_cost_min_usd} - $
                              {item.estimated_cost_max_usd}
                            </p>
                            {item.notes ? (
                              <p className="mt-1 text-xs text-(--text-muted)">
                                {item.notes}
                              </p>
                            ) : null}
                            <RepairItemPhotoRefs
                              photos={repairItemPhotoNumbers(
                                item,
                                (display.gallery_damage_by_image ?? []).map(
                                  (photo) => photo.image_url,
                                ),
                              )}
                              t={t}
                              className="mt-1"
                            />
                            {item.repair_details_url ? (
                              <a
                                href={item.repair_details_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-(--accent) hover:underline"
                              >
                                {t("aiCheck.openRepairDetails")}
                                <span className="button-icon-motion-external inline-flex shrink-0">
                                  <ArrowUpRight
                                    className="h-3.5 w-3.5"
                                    aria-hidden
                                  />
                                </span>
                              </a>
                            ) : null}
                            <RepairPartSources item={item} analysisId={result.repair_analysis_id} index={index} vehicleTitle={result.listing_title} />
                          </m.div>
                        ))}
                        <p className="lg:col-span-2 text-sm font-semibold text-(--text-primary)">
                          {t("aiCheck.totalEstimatedRepairCost")}: $
                          {result.repair_total_min_usd ?? 0} - $
                          {result.repair_total_max_usd ?? 0}
                        </p>
                        {result.price_verdict ? (
                          <div className="lg:col-span-2">
                            <PriceVerdictGauge
                              verdict={result.price_verdict}
                              source={result.price_verdict_source}
                              archivedFinalBidComparables={
                                result.archived_final_bid_lookup?.status === "available" ||
                                result.price_verdict_source === "archived_final_bid_quantiles"
                                  ? result.archived_final_bid_comparables
                                  : null
                              }
                              buyNowPrice={result.buy_now_price_usd}
                              buyNowPriceCoefficient={
                                result.buy_now_price_coefficient
                              }
                              currentBidPrice={result.current_bid_usd}
                              className="mt-3"
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <p className="mt-1.5 leading-relaxed text-(--text-muted)">
                          {display.repair_recommendations?.trim() || "—"}
                        </p>
                        {result.repairability === "parts_only" ? (
                          <NotRepairablePriceNotice className="mt-3" />
                        ) : null}
                      </>
                    )}
                  </m.div>
                ) : null}
                <p className="text-xs text-(--text-faint)">
                  {t("aiCheck.disclaimer")}
                </p>
              </div>
            </m.div>
          ) : null}

          {showStandardGallerySection ? (
            display.gallery_damage_by_image &&
            display.gallery_damage_by_image.length > 0 ? (
              <PerPhotoDamageSection
                items={display.gallery_damage_by_image}
                repairItems={
                  result.repairability === "parts_only"
                    ? []
                    : (display.repair_items ?? [])
                }
                listingTitle={result.listing_title}
                interior360Url={result.interior_360_image_url}
              />
            ) : (
              <ScrollReveal>
                <ListingPhotoGallery
                  image_urls={result.image_urls}
                  ai_image_url={result.ai_image_url}
                  listingTitle={result.listing_title}
                />
              </ScrollReveal>
            )
          ) : null}
        </section>
      ) : null}

      {/* Out-of-checks modal */}
      <AppModal
        open={showNoChecksModal}
        onClose={() => setShowNoChecksModal(false)}
        ariaLabelledBy="no-checks-title"
        ariaDescribedBy="no-checks-body"
        backdropLabel={t("common.close")}
      >
        <div className="relative w-full max-w-md rounded-2xl border border-(--border) bg-(--bg-card) p-8 text-center space-y-5 shadow-2xl">
          <m.div
            className="mx-auto w-14 h-14 rounded-full bg-(--status-danger-bg) border border-(--status-danger-border) flex items-center justify-center"
            animate={shouldReduceMotion ? undefined : { scale: [1, 1.08, 1] }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <Zap className="w-7 h-7 text-(--status-danger)" />
          </m.div>
          <h2
            id="no-checks-title"
            className="text-xl font-bold text-(--text-primary)"
          >
            {t("aiCheck.noChecks.title")}
          </h2>
          <p
            id="no-checks-body"
            className="text-sm text-(--text-muted) leading-relaxed"
          >
            {isFreeChecksUser ? freeNoChecksBody : t("aiCheck.noChecks.body")}
          </p>
          <div className="flex flex-col gap-3 pt-2">
            {!isFreeChecksUser ? (
              <Button
                href="/buy-checks"
                variant="primary"
                fullWidth
                className="rounded-lg px-6 py-3"
              >
                {t("aiCheck.noChecks.cta")}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => setShowNoChecksModal(false)}
              className="rounded-xl px-6 py-3 text-(--text-muted) hover:text-(--text-primary)"
            >
              {t("common.close")}
            </Button>
          </div>
        </div>
      </AppModal>

      {/* Analysis confirmation modal — shown when the listing has limited
          visual data and/or a potentially undamaged source sortation. Uses
          the shared AppModal shell so safe-area padding and scroll handling
          match the rest of the app. */}
      <AppModal
        open={Boolean(pendingInsufficient)}
        onClose={cancelInsufficient}
        ariaLabelledBy="analysis-confirmation-title"
        ariaDescribedBy="analysis-confirmation-body"
        backdropLabel={t("common.close")}
      >
        <div className="relative w-full max-w-md rounded-2xl border border-(--border) bg-(--bg-card) p-8 text-center space-y-5 shadow-2xl">
          <m.div
            className="mx-auto w-14 h-14 rounded-full bg-(--status-warning-bg) border border-(--status-warning-border) flex items-center justify-center"
            animate={shouldReduceMotion ? undefined : { scale: [1, 1.08, 1] }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <AlertTriangle className="w-7 h-7 text-(--status-warning)" />
          </m.div>
          <h2
            id="analysis-confirmation-title"
            className="text-xl font-bold text-(--text-primary)"
          >
            {pendingConfirmationTitle}
          </h2>
          <p
            id="analysis-confirmation-body"
            className="text-sm text-(--text-muted) leading-relaxed"
          >
            {pendingConfirmationBody}
          </p>
          <div className="flex flex-col gap-3 pt-2">
            <Button
              type="button"
              variant="primary"
              fullWidth
              className="rounded-lg px-6 py-3"
              onClick={() => {
                const pending = pendingInsufficient;
                if (!pending) return;
                setPendingInsufficient(null);
                void runCheck(
                  pending.raw,
                  pending.syncUrl,
                  pending.listingIdHint,
                  pending.limitedImages,
                  pending.undamagedSortation,
                );
              }}
            >
              {t("aiCheck.insufficientImages.continue")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={cancelInsufficient}
              className="rounded-lg px-6 py-3 text-(--text-muted) hover:text-(--text-primary)"
            >
              {t("aiCheck.insufficientImages.cancel")}
            </Button>
          </div>
        </div>
      </AppModal>
    </div>
  );
}

export default function AiCheckPage() {
  return <AiCheckContent />;
}
